import { appApi } from '.';
import qs from 'query-string';
import { matchPath } from 'react-router-dom';
import * as Auth from 'aws-amplify/auth';

import {
    RoleNames,
    cognitoCustomAttributes,
    distributorType,
    docTypes,
    entityKey,
    paymentStatus,
    queryStringifyOptions,
    roleCategories,
    stepIDs,
    subscriptionDocStatus,
    userChallenges,
} from 'utils/constants';

import {
    authSchemas,
    capitalAccountSchemas,
    conventionsSchemas,
    distributorSchemas,
    documentSchemas,
    fundSchemas,
    networkSchemas,
    notificationSchemas,
    paymentSchemas,
    reportingSchemas,
    sgpSchemas,
    subscriptionSchemas,
    tunnelSchemas,
    userSchemas,
} from 'schemas';
import { userSelectors } from 'reducers';
import { tunnelErrorsSet } from 'reducers/tunnel';

import * as authApi from './auth';
import * as documentApi from './document';
import { getPaginationData, getPartialPaymentsTotal, purgeEmptyFields } from 'utils/helpers';
import { navigateTo } from 'utils/navigation';
import {
    getAdminEnrichments,
    getInvestorNecessaryEnrichments,
    getClientSpaceEnrichments,
    getRoleEnrichments,
} from 'schemas/auth';
import {
    companyConventionsUpdated,
    distributorCompanyUpdated,
    internalUserReplaced,
    investorProfileUpdated,
    newSubscriptionCreated,
    tunnelSubscriptionUpdated,
    userSubscriptionDeleted,
    userTunnelStepsUpdated,
    userUpdated,
    holdUpSet,
    logoutClosed,
    subscriptionFormStatusSet,
    registerResubmitted,
    logoutLoadingSet,
    shareCategorySet,
} from 'reducers/user';
import { customTunnelReducerCleared } from 'reducers/customTunnel';
import { createDocumentsIRI, getEntityIdFromIRI } from 'utils/helpers/iri';
import { exportData } from 'utils/helpers/documents';
import { sortArrayByKey } from 'utils/helpers/sorting';
import { deleteFromStorage, getFromStorage, saveInStorage } from 'utils/helpers/storage';

// sonarjs:S7763 - Needed because we also use those locally
export { authApi, documentApi };

const mainApi = appApi.injectEndpoints({
    endpoints: build => ({
        //#region COMMENTS

        createComment: build.mutation({
            query: payload => ({ url: '/comments', method: 'POST', data: payload }),
        }),

        //#endregion

        //#region BANKING STATEMENTS

        updateBankingData: build.mutation({
            // should invalidate me
            async queryFn(payload, { dispatch }, _extraOptions, fetchWithBQ) {
                // upload RIB document
                const docPayload = documentSchemas.sendDocuments(payload.rib, docTypes.RIB, 'rib');
                const ribDoc = await documentApi.sendDocuments(docPayload);

                // update banking statement
                const response = await fetchWithBQ({
                    url: `/banking_statements/${payload.id}`,
                    method: 'patch',
                    data: {
                        tempBankAccountNumber: payload.iban.replaceAll(/\s/g, ''),
                        tempBankIdentifierCode: payload.bic.replaceAll(/\s/g, ''),
                        tempRibDocument: `api/documents/${ribDoc[0].id}`,
                        updateRibForAllBankingStatements: payload.updateRibForAllBankingStatements,
                    },
                });

                // populate the store with current user
                const internalUser = await authApi.getMeEnriched(
                    authSchemas.getInvestorNecessaryEnrichments()
                );
                dispatch(internalUserReplaced(internalUser));

                return response;
            },
        }),

        updateBankingStatement: build.mutation({
            query: ({ id, ...payload }) => ({
                url: `/banking_statements/${id}`,
                method: 'PATCH',
                data: payload,
            }),
            invalidatesTags: ['me'],
        }),

        updateBankingStatementValidation: build.mutation({
            query: ({ value, id }) => ({
                url: `/banking_statements/${id}/validate`,
                method: 'POST',
                data: { validated: value },
            }),
        }),

        reinitiateDDASignature: build.mutation({
            query: id => ({
                url: `/banking_statements/${id}/direct-debit/initiate-signature`,
                method: 'POST',
                data: {},
            }),
        }),

        initiateNewDDASignature: build.mutation({
            query: id => ({
                url: `/banking_statements/${id}/direct-debit/sign-new-authorization`,
                method: 'POST',
                data: {},
            }),
        }),

        //#endregion

        //#region ROLES

        getRoles: build.query({
            query: () => ({
                url: `/public/roles?${qs.stringify(
                    { timestamp: Date.now() },
                    queryStringifyOptions
                )}`,
            }),
            keepUnusedDataFor: 300,
            providesTags: ['Role'],
        }),

        //#endregion

        //#region INVESTMENT FUNDS

        getInvestmentFund: build.query({
            query: payload => {
                if (typeof payload === 'object') {
                    return {
                        url: `/investment_funds/${payload.id}?${qs.stringify(
                            { ...payload, timestamp: Date.now() },
                            queryStringifyOptions
                        )}`,
                    };
                } else {
                    return { url: `/investment_funds/${payload}` };
                }
            },
            providesTags: (result, error, arg) => [{ type: 'Fund', id: arg }],
        }),
        getInvestmentFunds: build.query({
            query: payload => ({
                url: `/public/investment_funds?${qs.stringify(
                    { ...payload, timestamp: Date.now() },
                    queryStringifyOptions
                )}`,
            }),
            keepUnusedDataFor: 10,
            providesTags: (result, error, arg) => [
                'Fund',
                ...result.map(({ id }) => ({ type: 'Fund', id })),
            ],
        }),
        createInvestmentFund: build.mutation({
            async queryFn(payload, _api, _extraOptions, fetchWithBQ) {
                let createPayload = { ...payload };

                // Upload fund logo
                const [logoDoc] = await documentApi.sendDocuments(
                    documentSchemas.sendDocuments(payload.image, docTypes.INVESTMENT_FUND, 'logo')
                );
                createPayload.image = createDocumentsIRI(logoDoc.id);

                // Upload fund regulations
                const [regDoc] = await documentApi.sendDocuments(
                    documentSchemas.sendDocuments(
                        payload.fundRegulation,
                        docTypes.INVESTMENT_FUND,
                        'fund_document'
                    )
                );
                createPayload.fundRegulation = createDocumentsIRI(regDoc.id);

                // Upload merged DICI
                const [diciDoc] = await documentApi.sendDocuments(
                    documentSchemas.sendDocuments(
                        payload.mergedDici,
                        docTypes.INVESTMENT_FUND,
                        'fund_document'
                    )
                );
                createPayload.mergedDici = createDocumentsIRI(diciDoc.id);

                // Upload optional docs
                let docPromises = fundSchemas.updateInvestmentFundDocs(payload);

                Object.keys(docPromises).forEach(key => {
                    // always send new docs on creation
                    docPromises[key] = documentApi.sendDocuments(docPromises[key]);
                });

                const responses = await Promise.all(Object.values(docPromises));

                const docsPayload = {};
                Object.keys(docPromises).forEach((key, index) => {
                    docsPayload[key] = createDocumentsIRI(responses[index][0].id);
                });

                createPayload = { ...createPayload, ...docsPayload };

                // Create the new investment fund
                const response = await fetchWithBQ({
                    url: `/investment_funds`,
                    method: 'POST',
                    data: createPayload,
                });

                return response;
            },
            invalidatesTags: ['Fund'],
        }),
        updateInvestmentFund: build.mutation({
            async queryFn(payload, _api, _extraOptions, fetchWithBQ) {
                let patchPayload = { ...payload };
                patchPayload = fundSchemas.removeUnchangedDocs(payload);

                // @documentsOnly - flag used in FundDocuments component
                if (payload.documentsOnly) {
                    // upload optional documents
                    let docPromises = fundSchemas.updateInvestmentFundDocs(payload);
                    patchPayload = fundSchemas.deleteInvestmentFundDocs(payload, patchPayload);

                    Object.keys(docPromises).forEach(key => {
                        // start an async request to save the document
                        docPromises[key] = payload[`${key}Id`]
                            ? documentApi.replaceDocument(payload[`${key}Id`], docPromises[key])
                            : documentApi.sendDocuments(docPromises[key]);
                    });
                    const responses = await Promise.all(Object.values(docPromises));

                    const docsPayload = {};
                    Object.keys(docPromises).forEach((key, index) => {
                        docsPayload[key] = createDocumentsIRI(
                            responses[index][0].id || payload[`${key}Id`]
                        );
                    });

                    patchPayload = { ...patchPayload, ...docsPayload };
                } else {
                    // update fund core documents
                    if (!payload.image?.id && payload.imageId) {
                        // update fund logo
                        const formData = new FormData();
                        formData.append('file', payload.image);
                        formData.append('type', docTypes.INVESTMENT_FUND);
                        formData.append('subtype', 'logo');
                        await documentApi.replaceDocument(payload.imageId, formData);
                    }
                    if (!payload.fundRegulation?.id && payload.fundRegulationId) {
                        // update fund regulations doc
                        const formData = new FormData();
                        formData.append('file', payload.fundRegulation);
                        formData.append('type', docTypes.INVESTMENT_FUND);
                        formData.append('subtype', 'fund_document');
                        await documentApi.replaceDocument(payload.fundRegulationId, formData);
                    }
                    if (!payload.mergedDici?.id && payload.mergedDiciId) {
                        // update merged DICI doc
                        const formData = new FormData();
                        formData.append('file', payload.mergedDici);
                        formData.append('type', docTypes.INVESTMENT_FUND);
                        formData.append('subtype', 'fund_document');
                        await documentApi.replaceDocument(payload.mergedDiciId, formData);
                    }
                }
                // make the patch request
                const response = await fetchWithBQ({
                    url: `/investment_funds/${payload.id}`,
                    method: 'PATCH',
                    data: patchPayload,
                });

                return response;
            },
            invalidatesTags: ['Fund'],
        }),
        requestProductDistribution: build.mutation({
            async queryFn({ id, withSignature }, { dispatch }, _extraOptions, fetchWithBQ) {
                // make the initial request
                const response = await fetchWithBQ({
                    url: `/investment_funds/${id}/distribute-product`,
                    method: 'POST',
                    data: { withSignature },
                });
                // update current user in store
                const internalUser = await authApi.getMeEnriched(authSchemas.getAdminEnrichments());
                dispatch(companyConventionsUpdated(internalUser));

                return response;
            },
        }),
        assignPrivateFundToDistributors: build.mutation({
            query: ({ id, ...payload }) => ({
                url: `/investment_funds/${id}/assign-to-distributors`,
                method: 'PATCH',
                data: payload,
            }),
            invalidatesTags: ['Company'],
        }),
        cloneInvestmentFundTunnel: build.mutation({
            query: ({ id, ...payload }) => ({
                url: `/investment_funds/${id}/tunnels/clone`,
                method: 'POST',
                data: payload,
            }),
            invalidatesTags: ['Tunnel'],
        }),
        updateCustomTunnel: build.mutation({
            async queryFn({ ppId, pmId, ...payload }, _api, _extraOptions, fetchWithBQ) {
                const response = await fetchWithBQ({
                    url: `/tunnels/${ppId}`,
                    method: 'PATCH',
                    data: payload,
                });
                await fetchWithBQ({
                    url: `/tunnels/${pmId}`,
                    method: 'PATCH',
                    data: payload,
                });

                return response;
            },
            invalidatesTags: ['Tunnel'],
        }),

        //#endregion

        //#region REFERRALS

        getReferrals: build.query({
            query: payload => ({
                url: `/referrals?${qs.stringify(payload, queryStringifyOptions)}`,
            }),
            transformResponse: response => ({
                referrals: response.data,
                ...getPaginationData(response),
            }),
            keepUnusedDataFor: 60,
        }),

        createReferral: build.mutation({
            query: payload => ({ url: '/referrals/invite', method: 'POST', data: payload }),
        }),

        exportReferrals: build.mutation({
            async queryFn(payload, _api, _extraOptions, fetchWithBQ) {
                // make the initial request
                const { data: response } = await fetchWithBQ({
                    url: '/referrals/export',
                    method: 'POST',
                    data: payload,
                    responseType: 'arraybuffer',
                });

                // open downloaded file
                exportData(response.data, response.headers);

                return { data: null };
            },
        }),

        //#endregion

        //#region SUBSCRIPTION ALERTS

        createSubscriptionAlert: build.mutation({
            async queryFn(payload, { dispatch }, _extraOptions, fetchWithBQ) {
                // make the intended request
                await fetchWithBQ({
                    url: '/subscription_alerts',
                    method: 'POST',
                    data: payload,
                });
                // update current user in store
                const internalUser = await authApi.getMeEnriched(
                    authSchemas.getInvestorNecessaryEnrichments()
                );
                dispatch(userTunnelStepsUpdated(internalUser));

                return { data: null };
            },
        }),

        updateSubscriptionAlert: build.mutation({
            query: ({ id, ...payload }) => ({
                url: `/subscription_alerts/${id}`,
                method: 'PATCH',
                data: payload,
            }),
        }),

        //#endregion

        //#region CONVENTIONS

        getConventions: build.query({
            query: payload => ({
                url: `/conventions?${qs.stringify(payload, queryStringifyOptions)}`,
            }),
            keepUnusedDataFor: 300,
        }),

        createConvention: build.mutation({
            query: payload => ({
                url: '/conventions',
                method: 'POST',
                data: conventionsSchemas.createConvention(payload),
            }),
            invalidatesTags: ['Distributor'],
        }),

        updateConvention: build.mutation({
            query: ({ id, ...payload }) => ({
                url: `/conventions/${id}`,
                method: 'PATCH',
                data: payload,
            }),
        }),

        initiateConventionSignature: build.mutation({
            query: id => ({
                url: `/conventions/${id}/signature`,
                method: 'POST',
                data: {},
            }),
            invalidatesTags: ['Distributor'],
        }),

        //#endregion

        //#region USERS

        getUsers: build.query({
            query: payload => ({
                url: `/users/enriched?${qs.stringify(payload, queryStringifyOptions)}`,
            }),
            transformResponse: response => ({
                userList: response.result,
                lastPage: response.numPages,
            }),
            keepUnusedDataFor: 0,
            providesTags: ['User'],
        }),

        getUser: build.query({
            query: ({ id, enrichWith }) => ({
                url: `/users/enriched?${qs.stringify(
                    {
                        ...userSchemas.getUsers({ id }),
                        enrich_with: enrichWith || getInvestorNecessaryEnrichments().enrich_with,
                    },
                    queryStringifyOptions
                )}`,
            }),
            transformResponse: response => response.result[0],
            keepUnusedDataFor: 600,
            providesTags: ['User'],
        }),

        getUserLight: build.query({
            query: id => ({ url: `/users/${id}` }),
            keepUnusedDataFor: 0,
        }),

        getUserPortfolio: build.query({
            query: id => ({ url: `/users/${id}/portfolio` }),
            keepUnusedDataFor: 0,
        }),

        getAdmins: build.query({
            query: () => ({
                url: '/users/sgp',
            }),
            transformResponse: response => response.result,
        }),

        getDistributorUsers: build.query({
            query: payload => ({
                url: `/users/distributors?${qs.stringify(payload, queryStringifyOptions)}`,
            }),
            transformResponse: response => response.result,
        }),

        getInvestmentCharacteristics: build.query({
            query: payload =>
                `/investment_characteristics?${qs.stringify(payload, queryStringifyOptions)}`,
        }),

        deleteUser: build.mutation({
            query: id => ({ url: `/users/${id}`, method: 'DELETE' }),
            invalidatesTags: ['User', 'Subscription'],
        }),

        updateUserStatus: build.mutation({
            query: ({ id, ...payload }) => ({
                url: `/users/${id}/status`,
                method: 'PATCH',
                data: payload,
            }),
            invalidatesTags: ['User', 'Subscription'],
        }),

        inviteUser: build.mutation({
            query: payload => ({
                url: '/users/invite',
                method: 'POST',
                data: payload,
            }),
            invalidatesTags: ['User', 'Subscription'],
        }),

        resendInvitation: build.mutation({
            query: email => ({
                url: '/public/users/invite/resend',
                method: 'POST',
                data: { email: email.trim().toLowerCase() },
            }),
        }),

        updatePassword: build.mutation({
            query: payload => ({
                url: '/password',
                method: 'PUT',
                data: payload,
            }),
        }),

        impersonateUser: build.mutation({
            // should invalidate me
            async queryFn(
                { indirectSubscription, followupPath, userId },
                { getState, dispatch },
                _extraOptions,
                fetchWithBQ
            ) {
                // make the initial request
                await fetchWithBQ({
                    url: '/impersonation_sessions',
                    method: 'POST',
                    data: userSchemas.impersonate(userId),
                });

                // save original path to be able to return to it after impersonation ends
                saveInStorage('route', globalThis.location.pathname);

                // delay for DB to sync
                await new Promise(r => setTimeout(r, 800));

                // replace the internal user in store with the substitute
                let response = await authApi.getMeEnriched(getRoleEnrichments());
                if (
                    [RoleNames.PM_PROFESSIONNEL, RoleNames.PP_PROFESSIONNEL].includes(
                        response?.subscriptions?.[0]?.role?.name
                    )
                ) {
                    response = await authApi.getMeEnriched(getClientSpaceEnrichments());
                } else {
                    response = await authApi.getMeEnriched(getInvestorNecessaryEnrichments());
                }
                dispatch(internalUserReplaced(response));
                dispatch(appApi.util.resetApiState());

                if (indirectSubscription) {
                    // programatically advance to the next step in the tunnel
                    const tunnelSubscription = userSelectors.getTunnelSubscription(getState());

                    const { data: tunnels = [] } = await dispatch(
                        appApi.endpoints.getTunnels.initiate({
                            investmentFunds: tunnelSubscription?.investmentFund?.id || undefined,
                            role: tunnelSubscription?.role?.id || undefined,
                        })
                    );
                    const tunnelSteps = tunnels?.[0]?.tunnelPhases.reduce(
                        (acc, crt) => acc.concat(crt.tunnelSteps),
                        []
                    );
                    const secondStepId = tunnelSteps?.[1]?.step?.id || stepIDs.ACCOUNT_CREATION;
                    navigateTo(`/sign-up/${secondStepId}`);
                }

                if (followupPath) {
                    navigateTo(followupPath);
                }

                return { data: null };
            },
            invalidatesTags: ['Tunnel'],
        }),

        stopImpersonation: build.mutation({
            // should invalidate me
            async queryFn(_args, { getState, dispatch }, _extraOptions, fetchWithBQ) {
                // make the initial request
                await fetchWithBQ({
                    url: '/impersonation_sessions/stop',
                    method: 'POST',
                    data: {},
                });

                // delay for DB to sync
                await new Promise(r => setTimeout(r, 800));

                // TODO: use proper enrichments using the impersonatorRole value

                // replace the internal user in store with the substitute
                const response = await authApi.getMeEnriched(getAdminEnrichments());
                dispatch(internalUserReplaced(response));
                dispatch(appApi.util.resetApiState());

                const route = getFromStorage('route');
                if (route) {
                    navigateTo(route);
                    deleteFromStorage('route');
                }

                return { data: null };
            },
            invalidatesTags: ['Tunnel'],
        }),

        checkEmailAvailability: build.mutation({
            async queryFn(email, _api, _extraOptions, fetchWithBQ) {
                const { data } = await fetchWithBQ({
                    url: '/users/check-investor-availability',
                    method: 'POST',
                    data: { email },
                });

                return { data };
            },
            keepUnusedDataFor: 0,
        }),

        updateInvestmentCharacteristics: build.mutation({
            query: ({ id, ...payload }) => ({
                url: `/investment_characteristics/${id}`,
                method: 'PATCH',
                data: payload,
            }),
            invalidatesTags: ['User'],
        }),

        updateInvestorProfile: build.mutation({
            async queryFn({ id, ...payload }, { dispatch }, _extraOptions, fetchWithBQ) {
                // make the initial request
                const response = await fetchWithBQ({
                    url: `/investor_profiles/${id}`,
                    method: 'PATCH',
                    data: userSchemas.updateInvestorProfile({ id, ...payload }),
                });
                dispatch(investorProfileUpdated(response.data));

                return { data: null };
            },
        }),

        updateUser: build.mutation({
            async queryFn(
                { id, profile, ...rest },
                { getState, dispatch },
                _extraOptions,
                fetchWithBQ
            ) {
                const currentSubscription = userSelectors.getCurrentSubscription(getState());
                let payload = null;

                if (profile === 'PM') {
                    let documentsPayload = tunnelSchemas.legalRepresentativeDocPayload(rest);
                    let legalRepresentativeDocId = null;
                    if (documentsPayload) {
                        legalRepresentativeDocId = await documentApi.sendDocuments(
                            documentsPayload
                        );
                    }
                    payload = userSchemas.updateInvestorProfilePM(rest, legalRepresentativeDocId);
                } else if (profile === 'PP') {
                    payload = userSchemas.updateInvestorProfilePP(rest);
                } else {
                    payload = rest;
                }
                // make the initial request
                const response = await fetchWithBQ({
                    url: `/users/${id}`,
                    method: 'PATCH',
                    data: payload,
                });

                if (currentSubscription?.role?.category?.name?.includes(roleCategories.INVESTORS)) {
                    // update current user in store
                    const user = await authApi.getMeEnriched(getInvestorNecessaryEnrichments());
                    dispatch(userUpdated({ user, profile }));
                }

                return response.error ? { error: response.error } : { data: response.data };
            },
            invalidatesTags: ['User'],
        }),

        exportInvestors: build.mutation({
            query: payload => ({
                url: `/users/investors/export?${qs.stringify(
                    userSchemas.getUsers(payload),
                    queryStringifyOptions
                )}`,
            }),
        }),

        exportPortfolio: build.mutation({
            async queryFn(id, _api, _extraOptions, fetchWithBQ) {
                // make the initial request
                const response = await fetchWithBQ({
                    url: `/users/${id}/portfolio/generate`,
                    method: 'POST',
                    responseType: 'arraybuffer',
                    data: {},
                });

                if (!response.error) {
                    // open downloaded file
                    const { data, headers } = response.data;
                    exportData(data, headers, 'application/pdf');
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        //#endregion

        //#region DISTRIBUTOR COMPANIES

        getDistributorCompanies: build.query({
            query: payload =>
                `/distributor_companies/enriched?${qs.stringify(payload, queryStringifyOptions)}`,
            transformResponse: response => ({
                distributors: response.result,
                lastPage: response.numPages,
            }),
            providesTags: ['Company'],
            keepUnusedDataFor: 10,
        }),

        getDistributorCompany: build.query({
            query: id => `/distributor_companies/${id}`,
        }),

        createSpecialDistributor: build.mutation({
            async queryFn(payload, _api, _extraOptions, fetchWithBQ) {
                let logoDoc = null;
                let conventionsResponse = [];
                const errors = [];

                if (payload.type === distributorType.GROUPEMENT) {
                    const conventions = payload.products.map(p => p.convention);
                    const conventionsPayload = documentSchemas.sendDocuments(
                        conventions,
                        docTypes.CIF
                    );
                    conventionsResponse = await documentApi
                        .sendDocuments(conventionsPayload)
                        .catch(error => {
                            if (error.status === 415) {
                                errors.push({ code: 'unsupported_media_file', key: 'conventions' });
                            }
                        });
                }
                if (payload.logo) {
                    const logoPayload = documentSchemas.sendDocuments(
                        payload.logo,
                        docTypes.COMPANY,
                        'logo'
                    );
                    logoDoc = await documentApi.sendDocuments(logoPayload).catch(error => {
                        if (error.status === 415) {
                            errors.push({ code: 'unsupported_media_file', key: 'logo' });
                        }
                    });
                }
                let documentsPromisesObject =
                    distributorSchemas.distributorCompanyDocuments(payload);
                const documentPromises = Object.keys(documentsPromisesObject).map(key => {
                    return documentApi.sendDocuments(documentsPromisesObject[key]).catch(error => {
                        if (error.status === 415) {
                            errors.push({ code: 'unsupported_media_file', key });
                        }
                    });
                });
                const responses = await Promise.all(documentPromises);

                const docsPayload = {};
                Object.keys(documentsPromisesObject).forEach((key, index) => {
                    if (responses[index]) {
                        // For array fields, we need the whole array of uploaded documents
                        if (
                            key === 'beneficiariesIdentityDocuments' ||
                            key === 'legalRepresentativesIdentityDocuments'
                        ) {
                            docsPayload[key] = responses[index] || [];
                        } else {
                            docsPayload[key] = responses[index][0] || payload[key];
                        }
                    } else {
                        docsPayload[key] = payload[key];
                    }
                });

                if (errors.length > 0) {
                    return { error: { data: { detail: errors } } };
                }
                const createPayload = distributorSchemas.createDistributor(
                    { ...payload, ...docsPayload, logo: logoDoc?.[0] || logoDoc },
                    conventionsResponse
                );

                const response = await fetchWithBQ({
                    url: '/distributor_companies',
                    method: 'POST',
                    data: purgeEmptyFields(createPayload, null),
                });

                return response.data ? { data: null } : { error: response.error };
            },
            invalidatesTags: ['Distributor'],
        }),

        updateSpecialDistributor: build.mutation({
            async queryFn(payload, _api, _extraOptions, fetchWithBQ) {
                const { deletedDocuments, id } = payload;
                let logoDoc = payload.logo;
                let newResponse = [];
                let replacedResponse = [];
                const newConventions = payload.products
                    .filter(p => p.convention && !p.convention?.id && !p.replaced)
                    .map(p => p.convention);
                const replacedConventions = payload.products
                    .filter(p => p.convention && !p.convention?.id && p.replaced)
                    .map(p => p.convention);

                if (newConventions.length) {
                    const documentsPayload = documentSchemas.sendDocuments(
                        newConventions,
                        docTypes.CIF
                    );
                    newResponse = await documentApi.sendDocuments(documentsPayload);
                }
                if (replacedConventions.length) {
                    const documentsPayload = documentSchemas.sendDocuments(
                        replacedConventions,
                        docTypes.CIF
                    );
                    replacedResponse = await documentApi.sendDocuments(documentsPayload);
                }
                if (!logoDoc?.id && logoDoc) {
                    const logoPayload = documentSchemas.sendDocuments(
                        logoDoc,
                        docTypes.COMPANY,
                        'logo'
                    );
                    logoDoc = await documentApi.sendDocuments(logoPayload);
                }

                let documentsPromisesObject =
                    distributorSchemas.distributorCompanyDocuments(payload);
                Object.keys(documentsPromisesObject).forEach(key => {
                    documentsPromisesObject[key] = documentApi.sendDocuments(
                        documentsPromisesObject[key]
                    );
                });
                const responses = await Promise.all(Object.values(documentsPromisesObject));

                const docsPayload = {};
                Object.keys(documentsPromisesObject).forEach((key, index) => {
                    if (responses[index]) {
                        // For array fields, we need the whole array of uploaded documents
                        if (
                            key === 'beneficiariesIdentityDocuments' ||
                            key === 'legalRepresentativesIdentityDocuments'
                        ) {
                            docsPayload[key] = responses[index] || [];
                        } else {
                            docsPayload[key] = responses[index][0] || payload[key];
                        }
                    } else {
                        docsPayload[key] = payload[key];
                    }
                });

                const updatePayload = distributorSchemas.updateDistributor(
                    {
                        ...payload,
                        ...docsPayload,
                        beneficiariesIdentityDocuments: [
                            ...payload.beneficiariesIdentityDocuments,
                            ...(docsPayload.beneficiariesIdentityDocuments || []),
                        ],
                        legalRepresentativesIdentityDocuments: [
                            ...payload.legalRepresentativesIdentityDocuments,
                            ...(docsPayload.legalRepresentativesIdentityDocuments || []),
                        ],
                        logo: logoDoc?.[0] || logoDoc,
                    },
                    newResponse,
                    replacedResponse
                );

                const response = await fetchWithBQ({
                    url: `/distributor_companies/${id}`,
                    method: 'PATCH',
                    data: purgeEmptyFields(updatePayload, null),
                });

                if (deletedDocuments?.length > 0) {
                    await documentApi.removeDocuments(deletedDocuments);
                }

                return response.data ? { data: null } : { error: response.error };
            },
            invalidatesTags: ['Distributor'],
        }),

        updateDistributorCompany: build.mutation({
            async queryFn(action, { dispatch }, _extraOptions, fetchWithBQ) {
                const payload = distributorSchemas.updateDistributorCompany(action.payload);
                let logoDoc = null;

                const response = await fetchWithBQ({
                    url: `/distributor_companies/${payload.id}`,
                    method: 'PATCH',
                    data: payload,
                });

                if (action.payload.companyLogo) {
                    logoDoc = await fetchWithBQ({
                        url: `/documents/${getEntityIdFromIRI(
                            action.payload.companyLogo,
                            entityKey.DOCUMENT
                        )}`,
                    });
                }

                dispatch(
                    distributorCompanyUpdated({
                        ...response.data,
                        companyLogo: action.payload.companyLogo ? logoDoc : response.companyLogo,
                    })
                );
                action.callback?.(response);

                return { data: null };
            },
            invalidatesTags: ['Company', 'Distributor'],
        }),

        updateDistributorCommercial: build.mutation({
            query: payload => ({
                url: `/distributor_companies/${payload.id}/update-commercial`,
                method: 'POST',
                data: payload,
            }),
            invalidatesTags: ['Company', 'Distributor'],
        }),

        deleteDistributorCompany: build.mutation({
            query: id => ({ url: `/distributor_companies/${id}`, method: 'DELETE' }),
            invalidatesTags: ['Company', 'Distributor'],
        }),

        getDistributorCompanyEnriched: build.query({
            query: id =>
                `/distributor_companies/enriched?${qs.stringify(
                    distributorSchemas.getDistributorCompanyEnriched(id),
                    queryStringifyOptions
                )}`,
            transformResponse: response => response.result?.[0],
            providesTags: ['Distributor'],
        }),

        getAggregatedPromoterData: build.query({
            query: payload =>
                `/analytics/aggregated-promoter-data?${qs.stringify(
                    payload,
                    queryStringifyOptions
                )}`,
        }),

        exportDistributorCompanies: build.mutation({
            async queryFn(payload, _api, _extraOptions, fetchWithBQ) {
                // make the initial request
                const { data: response } = await fetchWithBQ({
                    url: `/distributor_companies/export-list?${qs.stringify(
                        payload,
                        queryStringifyOptions
                    )}`,
                    method: 'GET',
                    responseType: 'arraybuffer',
                });

                // open downloaded file
                exportData(response.data, response.headers);

                return { data: null };
            },
        }),

        importDistributorUsers: build.mutation({
            query: ({ id, file }) => ({
                url: `distributor_companies/${id}/users/import`,
                method: 'POST',
                headers: { 'Content-Type': 'multipart/form-data' },
                data: file,
            }),
        }),

        validateInscription: build.mutation({
            query: id => ({
                url: `/distributor_companies/${id}/validate-distributor-documents`,
                method: 'POST',
                data: {},
            }),
            invalidatesTags: ['Distributor'],
        }),

        //#endregion

        //#region DISTRIBUTOR BILLS

        getDistributorBills: build.query({
            query: () =>
                `/distributor_bills?${qs.stringify(
                    distributorSchemas.getDistributorBills(),
                    queryStringifyOptions
                )}`,
            providesTags: ['DistributorBills'],
        }),

        testDistributorBills: build.mutation({
            query: () => ({ url: '/distributor_bills/test', method: 'POST', data: {} }),
            invalidatesTags: ['DistributorBills'],
        }),

        simulateDistributorBills: build.mutation({
            async queryFn(_payload, _api, _extraOptions, fetchWithBQ) {
                // make the initial request
                const { data: response } = await fetchWithBQ({
                    url: '/distributor_bills/simulate',
                    method: 'POST',
                    data: {},
                    responseType: 'arraybuffer',
                });

                // open downloaded file
                exportData(response.data, response.headers);

                return { data: null };
            },
            invalidatesTags: ['DistributorBills'],
        }),

        activateDistributorBills: build.mutation({
            query: () => ({ url: '/distributor_bills/activate', method: 'POST', data: {} }),
            invalidatesTags: ['DistributorBills'],
        }),

        //#endregion

        //#region SUBSCRIPTIONS

        getSubscriptions: build.query({
            query: payload => ({
                url: `/subscriptions/enriched?${qs.stringify(
                    subscriptionSchemas.getSubscriptionList(payload),
                    queryStringifyOptions
                )}`,
            }),
            keepUnusedDataFor: 0,
            transformResponse: response => ({
                subscriptions: response.result,
                lastPage: response.numPages,
            }),
            providesTags: ['Subscription'],
        }),

        createSubscription: build.mutation({
            async queryFn(payload, { dispatch }, _extraOptions, fetchWithBQ) {
                // make the initial request
                await fetchWithBQ({ url: '/subscriptions', method: 'POST', data: payload });

                // update current user in store
                const internalUser = await authApi.getMeEnriched(
                    authSchemas.getInvestorNecessaryEnrichments()
                );
                dispatch(newSubscriptionCreated(internalUser));

                return { data: null };
            },
            invalidatesTags: ['Subscription'],
        }),

        createExternalSubscription: build.mutation({
            query: formData => ({
                url: '/subscriptions/external/servicer',
                method: 'POST',
                headers: { 'Content-Type': 'multipart/form-data' },
                data: formData,
            }),
            invalidatesTags: ['Subscription'],
        }),

        addIndirectSubscription: build.mutation({
            async queryFn(payload, { dispatch }, _extraOptions, fetchWithBQ) {
                // create the new subscription
                const subscriptionPayload = subscriptionSchemas.newIndirectSubscription(
                    payload.categoryId,
                    payload.userId
                );

                await fetchWithBQ({
                    url: '/subscriptions',
                    method: 'POST',
                    data: { ...subscriptionPayload },
                });
                dispatch(shareCategorySet(null));

                // request impersonation
                dispatch(
                    appApi.endpoints.impersonateUser.initiate({
                        followupPath: '/new-subscription',
                        userId: payload.userId,
                    })
                );

                return { data: null };
            },
            invalidatesTags: ['Subscription'],
        }),

        updateSubscription: build.mutation({
            query: ({ id, ...payload }) => ({
                url: `/subscriptions/${id}`,
                method: 'PATCH',
                data: payload,
            }),
            invalidatesTags: ['Subscription', 'User'],
        }),

        transferSubscription: build.mutation({
            query: payload => ({
                url: '/subscriptions/transfer',
                method: 'POST',
                data: subscriptionSchemas.transferSubscription(payload),
            }),
            invalidatesTags: ['Subscription'],
        }),

        moveSubscription: build.mutation({
            query: ({ id, ...payload }) => ({
                url: `/subscriptions/${id}/transfer-distributor`,
                method: 'POST',
                data: payload,
            }),
            invalidatesTags: ['Subscription', 'User'],
        }),

        suspendSubscription: build.mutation({
            query: id => ({
                url: `/subscriptions/${id}/suspend`,
                method: 'POST',
                data: {},
            }),
            invalidatesTags: ['Subscription', 'User'],
        }),

        reopenSubscription: build.mutation({
            query: id => ({
                url: `/subscriptions/${id}/reopen-tunnel`,
                method: 'POST',
                data: {},
            }),
            invalidatesTags: ['Subscription', 'User'],
        }),

        deleteSubscription: build.mutation({
            async queryFn({ id, refetchMe }, { dispatch }, _extraOptions, fetchWithBQ) {
                // make the initial request
                await fetchWithBQ({ url: `/subscriptions/${id}`, method: 'DELETE' });

                if (refetchMe) {
                    dispatch(userSubscriptionDeleted(id));
                }

                return { data: null };
            },
            invalidatesTags: ['Subscription'],
        }),

        exportSubscription: build.mutation({
            async queryFn(id, _api, _extraOptions, fetchWithBQ) {
                // make the initial request
                const response = await fetchWithBQ({
                    url: `/subscriptions/${id}/export`,
                    method: 'POST',
                    responseType: 'arraybuffer',
                    data: {},
                });

                if (!response.error) {
                    // open downloaded file
                    const { data, headers } = response.data;
                    exportData(data, headers);
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        exportSubscriptions: build.mutation({
            query: payload => ({
                url: `/subscriptions/export?${qs.stringify(
                    subscriptionSchemas.getSubscriptionList(payload),
                    queryStringifyOptions
                )}`,
            }),
        }),

        validateSubscription: build.mutation({
            query: id => ({
                url: `/subscriptions/${id}/validate-servicer`,
                method: 'POST',
                data: {},
            }),
            invalidatesTags: ['Subscription', 'User'],
        }),

        verifySubscription: build.mutation({
            query: id => ({
                url: `/subscriptions/${id}/validate-cif`,
                method: 'POST',
                data: {},
            }),
            invalidatesTags: ['Subscription', 'User'],
        }),

        //#endregion

        //#region SIGNATURES

        updateSignature: build.mutation({
            query: ({ id, ...payload }) => ({
                url: `/signatures/${id}`,
                method: 'PATCH',
                data: payload,
            }),
            invalidatesTags: ['Subscription', 'User'],
        }),

        //#endregion

        //#region CAPITAL CALLS

        getCapitalCalls: build.query({
            query: payload => ({
                url: `/fundraisings?${qs.stringify(payload, queryStringifyOptions)}`,
            }),
            providesTags: ['CapitalCall'],
        }),

        createCapitalCall: build.mutation({
            query: payload => ({ url: '/fundraisings', method: 'POST', data: payload }),
            invalidatesTags: ['CapitalCall'],
        }),

        updateCapitalCall: build.mutation({
            query: ({ id, ...payload }) => ({
                url: `/fundraisings/${id}`,
                method: 'PATCH',
                data: payload,
            }),
            invalidatesTags: ['CapitalCall'],
        }),

        deleteCapitalCall: build.mutation({
            query: id => ({ url: `/fundraisings/${id}`, method: 'DELETE' }),
            invalidatesTags: ['CapitalCall'],
        }),

        simulateCapitalCall: build.mutation({
            async queryFn(payload, _api, _extraOptions, fetchWithBQ) {
                // make the initial request
                const { data: response } = await fetchWithBQ({
                    url: '/fundraisings/simulate',
                    method: 'POST',
                    data: payload,
                    responseType: 'arraybuffer',
                });

                // open downloaded file
                exportData(response.data, response.headers);

                return { data: null };
            },
        }),

        testCapitalCall: build.mutation({
            query: payload => ({ url: '/fundraisings/test', method: 'POST', data: payload }),
            invalidatesTags: ['CapitalCall'],
        }),

        activateCapitalCall: build.mutation({
            query: payload => ({ url: '/fundraisings/activate', method: 'POST', data: payload }),
            invalidatesTags: ['CapitalCall'],
        }),

        downloadCapitalCallGeneratedDocuments: build.mutation({
            async queryFn(id, _api, _extraOptions, fetchWithBQ) {
                const response = await fetchWithBQ({
                    url: `/fundraisings/${id}/download-documents`,
                    method: 'GET',
                    data: {},
                    responseType: 'arraybuffer',
                });

                if (!response.error) {
                    // open downloaded file
                    const { data, headers } = response.data;
                    exportData(data, headers, 'application/x-zip-compressed');
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        traceCapitalCall: build.mutation({
            async queryFn(id, _api, _extraOptions, fetchWithBQ) {
                const response = await fetchWithBQ({
                    url: `/fundraisings/${id}/report`,
                    method: 'GET',
                    data: {},
                    responseType: 'arraybuffer',
                });

                if (!response.error) {
                    // open downloaded file
                    const { data, headers } = response.data;
                    exportData(data, headers);
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        sendCapitalCallDocuments: build.mutation({
            query: id => ({
                url: `/fundraisings/${id}/send-generated-documents`,
                method: 'POST',
                data: {},
            }),
        }),

        //#endregion

        //#region REPORTINGS

        getReportings: build.query({
            query: payload => ({
                url: `/reportings?${qs.stringify(payload, queryStringifyOptions)}`,
            }),
            providesTags: ['Reporting'],
        }),

        createReporting: build.mutation({
            async queryFn(payload, _api, _extraOptions, fetchWithBQ) {
                // save document
                const docPayload = documentSchemas.sendDocuments(payload.file, docTypes.REPORTING);
                const docResponse = await documentApi.sendDocuments(docPayload);

                // use newly saved document's id to create the payload
                const createPayload = reportingSchemas.createReporting({
                    ...payload,
                    docId: docResponse[0].id,
                });

                // make the base request
                await fetchWithBQ({
                    url: '/reportings',
                    method: 'POST',
                    data: createPayload,
                });

                return { data: null };
            },
            invalidatesTags: ['Reporting'],
        }),

        updateReporting: build.mutation({
            async queryFn(payload, _api, _extraOptions, fetchWithBQ) {
                const docChanged = payload.file instanceof File;
                let newDocId;

                if (docChanged) {
                    // save document
                    const docPayload = documentSchemas.sendDocuments(
                        payload.file,
                        docTypes.REPORTING
                    );
                    const docResponse = await documentApi.sendDocuments(docPayload);
                    newDocId = docResponse[0].id;
                }

                // use newly saved document's id to create the payload
                const updatePayload = reportingSchemas.updateReporting({
                    ...payload,
                    docId: newDocId,
                });

                // make the base request
                await fetchWithBQ({
                    url: `/reportings/${payload.id}`,
                    method: 'PATCH',
                    data: updatePayload,
                });

                return { data: null };
            },
            invalidatesTags: ['Reporting'],
        }),

        testReporting: build.mutation({
            query: id => ({ url: `/reportings/${id}/test`, method: 'POST', data: {} }),
            invalidatesTags: ['Reporting'],
        }),

        activateReporting: build.mutation({
            query: id => ({ url: `/reportings/${id}/activate`, method: 'POST', data: {} }),
            invalidatesTags: ['Reporting'],
        }),

        //#endregion

        //#region CAPITAL ACCOUNTS

        getCapitalAccounts: build.query({
            query: payload => ({
                url: `/capital_accounts?${qs.stringify(payload, queryStringifyOptions)}`,
            }),
            providesTags: ['CapitalAccount'],
        }),

        createCapitalAccount: build.mutation({
            async queryFn(_args, _api, _extraOptions, fetchWithBQ) {
                const { form, formDate } = _args;
                const items = Object.values(form);
                const promises = [];

                for (let capitalAccount of items) {
                    const payload = capitalAccountSchemas.addCapitalAccount(
                        capitalAccount,
                        formDate
                    );
                    promises.push(
                        fetchWithBQ({
                            url: '/capital_accounts',
                            method: 'POST',
                            data: payload,
                        })
                    );
                }

                await Promise.all(promises);

                return { data: null };
            },
            invalidatesTags: ['CapitalAccount'],
        }),

        updateCapitalAccount: build.mutation({
            async queryFn(_args, _api, _extraOptions, fetchWithBQ) {
                const { form, formDate } = _args;
                const items = Object.values(form);
                const promises = [];

                for (let capitalAccount of items) {
                    const payload = capitalAccountSchemas.addCapitalAccount(
                        capitalAccount,
                        formDate
                    );
                    promises.push(
                        fetchWithBQ({
                            url: `/capital_accounts/${capitalAccount.id}`,
                            method: 'PATCH',
                            data: payload,
                        })
                    );
                }

                await Promise.all(promises);

                return { data: null };
            },
            invalidatesTags: ['CapitalAccount'],
        }),

        testCapitalAccount: build.mutation({
            query: id => ({
                url: `/investment_funds/${id}/capital-accounts/test`,
                method: 'POST',
                data: {},
            }),
            invalidatesTags: ['CapitalAccount'],
        }),

        simulateCapitalAccount: build.mutation({
            async queryFn(id, _api, _extraOptions, fetchWithBQ) {
                // make the initial request
                const { data: response } = await fetchWithBQ({
                    url: `/investment_funds/${id}/capital-accounts/simulate`,
                    method: 'POST',
                    data: {},
                    responseType: 'arraybuffer',
                });

                if (response.data) {
                    // open downloaded file
                    exportData(response.data, response.headers);
                }
                return { data: null };
            },
        }),

        activateCapitalAccount: build.mutation({
            query: id => ({
                url: `/investment_funds/${id}/capital-accounts/activate`,
                method: 'POST',
                data: {},
            }),
            invalidatesTags: ['CapitalAccount'],
        }),

        //#endregion

        //#region NOTIFICATIONS

        getNotifications: build.query({
            query: payload => ({
                url: `/notifications?${qs.stringify(
                    notificationSchemas.getNotifications(payload),
                    queryStringifyOptions
                )}`,
            }),
            transformResponse: response => ({
                notifications: response.data,
                ...getPaginationData(response),
            }),
            keepUnusedDataFor: 600,
            providesTags: ['Notification'],
        }),

        getNotification: build.query({
            query: id => ({ url: `/notifications/${id}` }),
            transformResponse: response => response.data,
            keepUnusedDataFor: 600,
        }),

        createNotification: build.mutation({
            query: payload => ({
                url: '/notifications',
                method: 'POST',
                data: notificationSchemas.sendNotification(payload),
            }),
            invalidatesTags: ['Notification'],
        }),

        createMassNotification: build.mutation({
            async queryFn(
                { filters: { page, ...filters }, attachment, ...payload },
                { dispatch, getState },
                _extraOptions,
                fetchWithBQ
            ) {
                // get the subscriptions to send the message to
                const subscriptions = await fetchWithBQ({
                    url: `/subscriptions/enriched?${qs.stringify(
                        subscriptionSchemas.getSubscriptionList({ ...filters, pagination: false }),
                        queryStringifyOptions
                    )}`,
                    method: 'GET',
                });

                let attachmentDoc;

                // upload the file
                if (attachment) {
                    const filePayload = documentSchemas.sendDocuments(attachment, docTypes.OTHER);
                    attachmentDoc = await documentApi.sendDocuments(filePayload);
                }

                const userIds = new Set(subscriptions.data.result.map(s => s.user.id));

                const messagePayload = notificationSchemas.sendMassNotification({
                    documentId: attachmentDoc?.[0]?.id,
                    receivers: Array.from(userIds),
                    ...payload,
                });

                const response = await fetchWithBQ({
                    url: '/notifications',
                    method: 'POST',
                    data: messagePayload,
                });

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        sendSMS: build.mutation({
            query: payload => ({
                url: '/notifications/sms',
                method: 'POST',
                data: notificationSchemas.sendSMS(payload),
            }),
            invalidatesTags: ['Notification'],
        }),

        requestUpcomingProductNotification: build.mutation({
            query: () => ({
                url: '/notifications/upcoming-products-distributors',
                method: 'POST',
                data: {},
            }),
        }),

        //#endregion

        //#region PAYMENTS

        getPayments: build.query({
            query: payload => ({
                url: `/payments/enriched?${qs.stringify(
                    paymentSchemas.getEnhancedPayments(payload),
                    queryStringifyOptions
                )}`,
            }),
            transformResponse: response => ({
                payments: response.result,
                lastPage: response.numPages,
            }),
            keepUnusedDataFor: 600,
            providesTags: ['Payment'],
        }),

        createPartialPayment: build.mutation({
            async queryFn(_args, _api, _extraOptions, fetchWithBQ) {
                const { payment } = _args;
                const payload = paymentSchemas.addPartialPayment(_args);
                await fetchWithBQ({
                    url: `/payments/${payment.id}/partial`,
                    method: 'POST',
                    data: payload,
                });
                const totalPartialPayments = getPartialPaymentsTotal(payment);

                if (totalPartialPayments + payload.amount === payment.amount) {
                    // set payment to CONFIRMED
                    await fetchWithBQ({
                        url: `/payments/${payment.id}/partial`,
                        method: 'PATCH',
                        data: { id: payment.id, status: paymentStatus.CONFIRMED },
                    });
                }

                return { data: null };
            },
            invalidatesTags: ['Payment'],
        }),

        updatePayment: build.mutation({
            query: payload => ({
                url: `/payments/${payload.id}`,
                method: 'PATCH',
                data: payload,
            }),
            invalidatesTags: ['Payment'],
        }),

        createDirectDebit: build.mutation({
            query: payload => ({
                url: '/payments/direct-debit/generate',
                method: 'POST',
                data: payload,
            }),
            invalidatesTags: ['Payment'],
        }),

        exportPayments: build.mutation({
            async queryFn(payload, _api, _extraOptions, fetchWithBQ) {
                // make the initial request
                const { data: response } = await fetchWithBQ({
                    url: `/payments/export?${qs.stringify(
                        paymentSchemas.getEnhancedPayments(payload),
                        queryStringifyOptions
                    )}`,
                    method: 'GET',
                    responseType: 'arraybuffer',
                });

                // open downloaded file
                exportData(response.data, response.headers);

                return response;
            },
        }),

        //#endregion

        //#region NETROWKS

        getNetworks: build.query({
            query: payload => ({
                url: `/networks?${qs.stringify(payload, queryStringifyOptions)}`,
            }),
            providesTags: ['Network'],
        }),

        getNetwork: build.query({
            query: id => ({ url: `/networks/${id}` }),
            invalidatesTags: ['Network'],
            keepUnusedDataFor: 0,
        }),

        createNetwork: build.mutation({
            query: payload => ({
                url: '/networks',
                method: 'POST',
                data: networkSchemas.createNetwork(payload),
            }),
            invalidatesTags: ['Network'],
        }),

        updateNetwork: build.mutation({
            query: payload => ({
                url: `/networks/${payload.id}`,
                method: 'PUT',
                data: networkSchemas.updateNetwork(payload),
            }),
            invalidatesTags: ['Network'],
        }),

        deleteNetwork: build.mutation({
            query: id => ({ url: `/networks/${id}`, method: 'DELETE' }),
            invalidatesTags: ['Network'],
        }),

        //#endregion

        //#region DOCUMENTS

        getDocuments: build.query({
            query: payload => ({
                url: `/documents?${qs.stringify(payload, queryStringifyOptions)}`,
            }),
            providesTags: ['Document'],
            keepUnusedDataFor: 0,
        }),

        getDocumentAudits: build.query({
            query: payload => ({
                url: `/document_audits?${qs.stringify(payload, queryStringifyOptions)}`,
            }),
            providesTags: ['DocumentAudit'],
            keepUnusedDataFor: 0,
        }),

        getDocument: build.query({
            query: id => ({ url: `/documents/${id}` }),
            providesTags: ['Document'],
            keepUnusedDataFor: 0,
        }),

        createDocuments: build.mutation({
            async queryFn(payload, { dispatch }, _extraOptions, fetchWithBQ) {
                const { formData, updateUser } = payload;

                const response = await fetchWithBQ({
                    url: '/documents/multi',
                    method: 'POST',
                    headers: { 'Content-Type': 'multipart/form-data' },
                    data: formData,
                });

                if (response.error) {
                    if (response.error.status === 415) {
                        return { error: 'Unsupported Media Type (415)' };
                    }
                    return { error: response.error };
                }

                if (updateUser) {
                    const user = await authApi.getMeEnriched(getClientSpaceEnrichments());
                    dispatch(internalUserReplaced(user));
                }

                return { data: null };
            },
            invalidatesTags: ['Document', 'me'],
        }),

        replaceDocument: build.mutation({
            async queryFn(payload, { dispatch }, _extraOptions, fetchWithBQ) {
                const { id, formData, updateUser } = payload;

                const response = await fetchWithBQ({
                    url: `/documents/replace/${id}`,
                    method: 'POST',
                    headers: { 'Content-Type': 'multipart/form-data' },
                    data: formData,
                });

                if (response.error) {
                    return { error: response.error };
                }

                if (updateUser) {
                    const user = await authApi.getMeEnriched(getClientSpaceEnrichments());
                    dispatch(internalUserReplaced(user));
                }

                return { data: null };
            },
            invalidatesTags: ['Document', 'DocumentAudit', 'me'],
        }),

        deleteDocument: build.mutation({
            query: id => ({ url: `/documents/${id}`, method: 'DELETE' }),
            invalidatesTags: ['Document'],
        }),

        validateDocument: build.mutation({
            query: ({ id, ...payload }) => ({
                url: `/documents/validate/${id}`,
                method: 'POST',
                data: payload,
            }),
            invalidatesTags: ['Document', 'Distributor'],
        }),

        startReplaceIdVerification: build.mutation({
            query: id => ({
                url: `/documents/replace/id/1/${id}`,
                method: 'POST',
                data: {},
            }),
        }),

        sendReplaceIdVerification: build.mutation({
            query: id => ({
                url: `/documents/replace/id/2/${id}`,
                method: 'POST',
                data: {},
            }),
        }),

        startReplaceAddressVerification: build.mutation({
            query: ({ id, addressDocumentType }) => ({
                url: `/documents/replace/address/1/${id}`,
                method: 'POST',
                data: tunnelSchemas.startAddressVerification(addressDocumentType),
            }),
        }),

        sendReplaceAddressVerification: build.mutation({
            query: id => ({
                url: `/documents/replace/address/2/${id}`,
                method: 'POST',
                data: {},
            }),
        }),

        //#endregion

        //#region SHARE CATEGORIES

        getShareCategories: build.query({
            query: payload => ({
                url: `/share_categories?${qs.stringify(
                    { ...payload, pagination: false },
                    queryStringifyOptions
                )}`,
            }),
            providesTags: ['ShareCategory'],
        }),

        createShareCategory: build.mutation({
            async queryFn(payload, { dispatch }, _extraOptions, fetchWithBQ) {
                // Kick off uploads for subcategories with DICI File
                const uploadPromises = payload.shareSubcategories.map(sc => {
                    if (sc.dici instanceof File) {
                        const diciPayload = documentSchemas.sendDocuments(
                            sc.dici,
                            docTypes.INVESTMENT_FUND,
                            'fund_document'
                        );

                        // Start upload immediately
                        return documentApi.sendDocuments(diciPayload).then(docResponse => ({
                            ...sc,
                            dici: docResponse?.[0]?.id
                                ? `/api/documents/${docResponse[0].id}`
                                : null,
                        }));
                    }

                    // If no upload needed, return it as-is (synchronous)
                    return Promise.resolve(sc);
                });

                // Wait for all uploads to settle
                const updatedSubcategories = await Promise.all(uploadPromises);

                // Use updated subcategories in POST
                const response = await fetchWithBQ({
                    url: '/share_categories',
                    method: 'POST',
                    data: { ...payload, shareSubcategories: updatedSubcategories },
                });

                return response.error ? { error: response.error } : { data: null };
            },
            invalidatesTags: ['ShareCategory'],
        }),

        updateShareCategory: build.mutation({
            query: ({ id, ...payload }) => ({
                url: `/share_categories/${id}`,
                method: 'PUT',
                data: payload,
            }),
            invalidatesTags: ['ShareCategory'],
        }),

        deleteShareCategory: build.mutation({
            query: id => ({
                url: `/share_categories/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ShareCategory'],
        }),

        getShareSubcategories: build.query({
            query: payload => ({
                url: `/share_subcategories?${qs.stringify(
                    { ...payload, pagination: false },
                    queryStringifyOptions
                )}`,
            }),
            providesTags: ['ShareSubcategory'],
        }),

        getShareSubcategory: build.query({
            query: id => ({ url: `/share_subcategories/${id}` }),
            providesTags: ['ShareSubcategory'],
        }),

        createShareSubcategory: build.mutation({
            async queryFn(payload, _api, _extraOptions, fetchWithBQ) {
                let diciValue = null;
                if (payload.dici instanceof File) {
                    const diciPayload = documentSchemas.sendDocuments(
                        payload.dici,
                        docTypes.INVESTMENT_FUND,
                        'fund_document'
                    );

                    // Start upload immediately
                    const docResponse = await documentApi.sendDocuments(diciPayload);
                    diciValue = docResponse?.[0]?.id ? `/api/documents/${docResponse[0].id}` : null;
                }

                // Use updated subcategories in POST
                const response = await fetchWithBQ({
                    url: '/share_subcategories',
                    method: 'POST',
                    data: { ...payload, dici: diciValue },
                });

                return response.error ? { error: response.error } : { data: null };
            },
            invalidatesTags: ['ShareSubcategory', 'ShareCategory'],
        }),

        updateShareSubcategory: build.mutation({
            async queryFn({ id, diciId, ...payload }, _api, _extraOptions, fetchWithBQ) {
                let diciValue;

                if (payload.dici instanceof File) {
                    // Start upload immediately
                    if (diciId) {
                        const diciPayload = documentSchemas.replaceDocument(payload.dici);
                        await documentApi.replaceDocument(diciId, diciPayload);
                    } else {
                        const diciPayload = documentSchemas.sendDocuments(
                            payload.dici,
                            docTypes.INVESTMENT_FUND,
                            'fund_document'
                        );
                        const docResponse = await documentApi.sendDocuments(diciPayload);
                        diciValue = docResponse?.[0]?.id
                            ? `/api/documents/${docResponse[0].id}`
                            : null;
                    }
                }

                // Use updated subcategories in POST
                const response = await fetchWithBQ({
                    url: `/share_subcategories/${id}`,
                    method: 'PATCH',
                    data: { ...payload, dici: diciValue },
                });

                return response.error ? { error: response.error } : { data: null };
            },
            invalidatesTags: ['ShareSubcategory', 'ShareCategory'],
        }),

        deleteShareSubcategory: build.mutation({
            query: id => ({
                url: `/share_subcategories/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ShareSubcategory', 'ShareCategory'],
        }),

        //#endregion

        //#region CLOSINGS

        getClosings: build.query({
            query: payload => ({
                url: `/closings?${qs.stringify(payload, queryStringifyOptions)}`,
            }),
            providesTags: ['Closing'],
        }),

        createClosing: build.mutation({
            query: payload => ({ url: '/closings', method: 'POST', data: payload }),
            invalidatesTags: ['Closing'],
        }),

        updateClosing: build.mutation({
            query: ({ id, ...payload }) => ({
                url: `/closings/${id}`,
                method: 'PATCH',
                data: payload,
            }),
            invalidatesTags: ['Closing'],
        }),

        deleteClosing: build.mutation({
            query: id => ({ url: `/closings/${id}`, method: 'DELETE' }),
            invalidatesTags: ['Closing'],
        }),

        simulateClosing: build.mutation({
            async queryFn(payload, _api, _extraOptions, fetchWithBQ) {
                // make the initial request
                const { data: response } = await fetchWithBQ({
                    url: '/closings/simulate',
                    method: 'POST',
                    data: payload,
                    responseType: 'arraybuffer',
                });

                // open downloaded file
                exportData(response.data, response.headers);

                return { data: null };
            },
        }),

        testClosing: build.mutation({
            query: payload => ({ url: '/closings/test', method: 'POST', data: payload }),
            invalidatesTags: ['Closing'],
        }),

        activateClosing: build.mutation({
            query: payload => ({ url: '/closings/activate', method: 'POST', data: payload }),
            invalidatesTags: ['Closing'],
        }),

        //#endregion

        //#region TUNNEL

        checkDistributorPedagogicStep: build.mutation({
            async queryFn(payload, { dispatch }, _extraOptions, fetchWithBQ) {
                // make the initial request
                const response = await fetchWithBQ({
                    url: '/register/48',
                    method: 'POST',
                    data: {},
                });

                // refresh store user data
                const internalUser = await authApi.getMeEnriched(
                    authSchemas.getInvestorNecessaryEnrichments()
                );
                dispatch(tunnelSubscriptionUpdated(internalUser));

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        getTunnelMessages: build.query({
            query: () => ({ url: '/translations/messages.json' }),
            transformResponse: response => response?.translations?.fr?.messages,
            keepUnusedDataFor: 3600,
        }),

        getTunnel: build.query({
            query: id => ({ url: `/tunnels/${id}` }),
            providesTags: ['Tunnel'],
            keepUnusedDataFor: 0,
        }),

        getTunnels: build.query({
            query: payload => ({
                url: `/public/tunnels?${qs.stringify({ ...payload, timestamp: Date.now() })}`,
            }),
            transformResponse: response =>
                response.map(tunnel => ({
                    ...tunnel,
                    tunnelPhases: tunnel.tunnelPhases.map(ph => ({
                        ...ph,
                        tunnelSteps: ph.tunnelSteps.map(ts => ({
                            ...ts,
                            questions: ts.questions?.sort(sortArrayByKey('order')).map(q => ({
                                ...q,
                                answers: q.answers?.sort(sortArrayByKey('order')),
                            })),
                        })),
                    })),
                })),
            providesTags: ['Tunnel'],
            keepUnusedDataFor: 0,
        }),

        deletePendingTunnel: build.mutation({
            query: id => ({
                url: `/investment_funds/${id}/tunnels/remove`,
                method: 'POST',
                data: {},
            }),
            invalidatesTags: ['Tunnel'],
        }),

        activateCustomTunnel: build.mutation({
            query: id => ({
                url: `/investment_funds/${id}/tunnels/activate`,
                method: 'POST',
                data: {},
            }),
            invalidatesTags: ['Tunnel'],
        }),

        //#endregion

        //#region AUTHENTICATION

        setPassword: build.mutation({
            async queryFn(payload, { dispatch }, _extraOptions, fetchWithBQ) {
                // make the initial request
                const response = await fetchWithBQ({
                    url: '/public/password/set',
                    method: 'POST',
                    data: authSchemas.setPassword(payload),
                });

                if (!response.error && response.data.email) {
                    await Auth.signIn({
                        username: response.data.email,
                        password: payload.password,
                    });

                    // confirm email in our DB
                    await fetchWithBQ({
                        url: '/users/email/confirm',
                        method: 'POST',
                        data: {},
                    });
                    await fetchWithBQ({
                        url: '/users/email/verify',
                        method: 'POST',
                        data: {},
                    });

                    // register login in our DB
                    await fetchWithBQ({ url: '/post-login', method: 'POST' });

                    // populate the store with current user
                    const internalUser = await authApi.getMeEnriched(
                        authSchemas.getInvestorNecessaryEnrichments()
                    );
                    dispatch(internalUserReplaced(internalUser));

                    // TODO: find a solution to have this navigate dinamically to the proper step
                    navigateTo('/sign-up/2');
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        createPassword: build.mutation({
            async queryFn(payload, { dispatch }, _extraOptions, fetchWithBQ) {
                dispatch(holdUpSet(true));

                const response = await Auth.confirmSignIn({
                    challengeResponse: payload.password, // the new password
                });
                if (response.nextStep?.signInStep !== 'DONE') {
                    // TODO: implement flow for users that have 2FA activated
                    return { data: null };
                }

                try {
                    // at this point the user would be authenticated
                    // only if by some mistake the user is still not fully authenticated
                    await Auth.autoSignIn();
                } catch (error) {
                    // ignore this error, it means the autoSignIn was not required
                    console.log(error.name);
                }

                // confirm email in our DB
                await fetchWithBQ({
                    url: '/users/email/confirm',
                    method: 'POST',
                    data: {},
                });
                await fetchWithBQ({
                    url: '/users/email/verify',
                    method: 'POST',
                    data: {},
                });

                // register login in our DB
                await fetchWithBQ({ url: '/post-login', method: 'POST' });

                // populate the store with current user
                const userAttributes = await Auth.fetchUserAttributes();
                const cognitoRole = userAttributes[cognitoCustomAttributes.ROLE];
                const internalUser = [
                    RoleNames.PM_PROFESSIONNEL,
                    RoleNames.PP_PROFESSIONNEL,
                ].includes(cognitoRole)
                    ? await authApi.getMeEnriched(getClientSpaceEnrichments())
                    : await authApi.getMeEnriched(getAdminEnrichments());
                dispatch(internalUserReplaced(internalUser));

                // clear store
                if (!response.error) {
                    const defaultRoute =
                        internalUser?.subscriptions?.[0].role.category.name ===
                            roleCategories.ADMIN_DISTRIBUTORS ||
                        internalUser?.subscriptions?.[0].role.category.name ===
                            roleCategories.DISTRIBUTORS
                            ? '/homepage'
                            : '/dashboard';
                    dispatch(holdUpSet(false));
                    dispatch(
                        mainApi.internalActions.removeMutationResult({
                            fixedCacheKey: 'cognito-sign-in',
                        })
                    );
                    navigateTo(defaultRoute);
                }

                return response.error ? { error: response.error } : { data: null };
            },
        }),

        forgotPassword: build.mutation({
            async queryFn(email, _api, _extraOptions) {
                try {
                    await Auth.resetPassword({ username: email });
                    return { data: null };
                } catch (error) {
                    if (error.name === 'UserNotFoundException') {
                        return { error: 'not_found' };
                    }
                    if (error.name === 'InvalidParameterException') {
                        return { error: 'not_verified' };
                    }
                    return { error };
                }
            },
        }),

        resetPassword: build.mutation({
            async queryFn({ email, code, password }, _api, _extraOptions) {
                try {
                    await Auth.confirmResetPassword({
                        username: email,
                        confirmationCode: code,
                        newPassword: password,
                    });
                    return { data: null };
                } catch (error) {
                    return { error };
                }
            },
        }),

        resendSignUp: build.mutation({
            async queryFn(email, _api, _extraOptions) {
                try {
                    await Auth.resendSignUpCode({ username: email.toLowerCase() });
                    return { data: null };
                } catch (error) {
                    return { error };
                }
            },
        }),

        confirmSignUp: build.mutation({
            async queryFn({ email, code }, _api, _extraOptions) {
                try {
                    await Auth.confirmSignUp({ username: email, confirmationCode: code });
                    return { data: null };
                } catch (error) {
                    return { error };
                }
            },
        }),

        signOut: build.mutation({
            async queryFn(redirect, { dispatch, getState }, _extraOptions, fetchWithBQ) {
                const internalUser = userSelectors.getInternalUser(getState());

                if (!internalUser) return;

                try {
                    await fetchWithBQ({ url: '/logout', method: 'POST', data: {} }); // logout on our backend
                    await Auth.signOut(); // logout from Cognito, also clears localStorage

                    dispatch(internalUserReplaced(null));
                    dispatch(customTunnelReducerCleared());

                    // clear remaining data in storage
                    globalThis.localStorage.clear();

                    // close dialog
                    dispatch(logoutClosed());

                    // Check if current path is not a public route (simplified check)
                    const currentPath = globalThis.location.pathname;
                    const isPublicRoute =
                        currentPath === '/sign-in' ||
                        currentPath === '/forgot-password' ||
                        currentPath === '/reset-password' ||
                        currentPath === '/setup-password' ||
                        currentPath === '/confirm-email' ||
                        currentPath.startsWith('/sign-up-');

                    if (!isPublicRoute || redirect) {
                        navigateTo('/sign-in');
                    }
                    dispatch(appApi.util.resetApiState());
                } catch (error) {
                    navigateTo('/sign-in');
                    return { error };
                }
                return { data: null };
            },
            invalidatesTags: ['Tunnel', 'Role'],
        }),

        getCurrentUser: build.query({
            async queryFn(
                subscriptionFormGeneratedFlag,
                { dispatch, getState },
                _extraOptions,
                fetchWithBQ
            ) {
                try {
                    await Auth.fetchUserAttributes();

                    let internalUser = await authApi.getMeEnriched(getRoleEnrichments());
                    let userRole = internalUser?.subscriptions?.[0].role?.name;

                    switch (userRole) {
                        case RoleNames.PP_PROFESSIONNEL:
                        case RoleNames.PM_PROFESSIONNEL:
                            internalUser = await authApi.getMeEnriched(getClientSpaceEnrichments());
                            break;
                        case RoleNames.SERVICER_ADMIN:
                            internalUser = await authApi.getMeEnriched(getAdminEnrichments());
                            break;
                        case RoleNames.SIGNATORY:
                            internalUser = await authApi.getMeEnriched(getAdminEnrichments());
                            break;
                        default:
                            internalUser = await authApi.getMeEnriched(
                                getInvestorNecessaryEnrichments()
                            );
                    }

                    dispatch(internalUserReplaced(internalUser));
                    if (subscriptionFormGeneratedFlag) {
                        dispatch(subscriptionFormStatusSet(subscriptionDocStatus.GENERATED));
                    }

                    return { data: null };
                } catch (error) {
                    return { error };
                }
            },
            keepUnusedDataFor: 0,
            providesTags: ['me'],
        }),

        cognitoSignIn: build.mutation({
            async queryFn(payload, { dispatch }, _extraOptions) {
                const { email, password } = payload;

                try {
                    const authResponse = await Auth.signIn({ username: email, password });
                    if (
                        authResponse.nextStep?.signInStep === userChallenges.SMS_CODE_REQUIRED ||
                        authResponse.nextStep?.signInStep === userChallenges.NEW_PASSWORD_REQUIRED
                    ) {
                        return { data: authResponse };
                    }
                    const userAttributes = await Auth.fetchUserAttributes();
                    if (!userAttributes[cognitoCustomAttributes.ROLE]) {
                        // a control call to see what enrichments we should use
                        // the cognito user is "busted" if script ever runs through here
                        let internalUser = await authApi.getMeEnriched(getRoleEnrichments());
                        let userRole = internalUser?.subscriptions?.[0].role;
                        if (userRole) {
                            // fixing the cognito user's attributes
                            await Auth.updateUserAttributes({
                                userAttributes: {
                                    [cognitoCustomAttributes.ROLE]: userRole.name,
                                    [cognitoCustomAttributes.ROLE_ID]: '' + userRole.id,
                                },
                            });
                        }
                    }
                    return { data: userAttributes };
                } catch (error) {
                    if (error === 403) {
                        dispatch(internalUserReplaced(null));
                        await Auth.signOut();
                    }
                    return { error };
                }
            },
        }),

        signIn: build.mutation({
            async queryFn(payload, { dispatch }, _extraOptions, fetchWithBQ) {
                let internalUser = await authApi.getMeEnriched(getRoleEnrichments());
                let userRole = internalUser?.subscriptions?.[0].role?.name;

                dispatch(logoutLoadingSet(true));

                let clientSpaceEnrichments = getClientSpaceEnrichments();
                const adminEnrichments = [RoleNames.ADMIN_CIF, RoleNames.PSI].includes(userRole)
                    ? getClientSpaceEnrichments()
                    : getAdminEnrichments();

                const response = await authApi.getMeEnriched(
                    [RoleNames.PP_PROFESSIONNEL, RoleNames.PM_PROFESSIONNEL].includes(userRole)
                        ? clientSpaceEnrichments
                        : adminEnrichments
                );

                if (response.availabilityStatus === 'inactive') {
                    dispatch(internalUserReplaced(null));
                    await Auth.signOut();
                } else {
                    dispatch(internalUserReplaced(response));

                    // update the roles in store
                    const roles = await fetchWithBQ({
                        url: `/public/roles?${qs.stringify(
                            { timestamp: Date.now() },
                            queryStringifyOptions
                        )}`,
                        method: 'GET',
                    });
                    dispatch(
                        appApi.util.updateQueryData('getRoles', undefined, () => [...roles.data])
                    );

                    await fetchWithBQ({ url: '/post-login', method: 'POST' });

                    if (!response.emailConfirmed && !response.impersonatedByUser) {
                        // confirm email in our DB
                        // any user that is able to reach this has already confirmed their email
                        // mostly for direct investors
                        // do not confirm if an impersonation session is active
                        // since investor users can be impersonated by distributors without having confirmed the email
                        // - before transfering the subscription to investor
                        await fetchWithBQ({
                            url: '/users/email/confirm',
                            method: 'POST',
                            data: {},
                        });
                    }
                }
                dispatch(logoutLoadingSet(false));
                return { data: null };
            },
            invalidatesTags: ['Tunnel', 'User', 'Distributor'],
        }),

        // MFA flow
        confirmSignIn: build.mutation({
            async queryFn(payload, _api, _extraOptions, fetchWithBQ) {
                // any errors will be cached by RTK-query and handled inside the component
                await Auth.confirmSignIn({ challengeResponse: payload.code });
                return { data: null };
            },
        }),

        signUp: build.mutation({
            async queryFn(payload, { dispatch, getState }, _extraOptions, fetchWithBQ) {
                const { resubmit, data } = payload;
                dispatch(tunnelErrorsSet([])); // remove tunnel errors

                const isCreatingSubscription = matchPath(
                    '/create-subscription/*',
                    globalThis.location.pathname
                );
                const subscription = userSelectors.getCurrentSubscription(getState());
                const subcategoryId = userSelectors.getShareSubcategoryId(getState());
                const isPP = data.roleName?.includes('PP');

                const userIsDistributor = [RoleNames.ADMIN_CIF, RoleNames.PSI].includes(
                    data.roleName
                );
                let registerPayload;
                let profileType;
                if (isPP) {
                    profileType = 'PP';
                    registerPayload = authSchemas.register({
                        ...data,
                        subcategoryId: data.subcategoryId || subcategoryId,
                    });
                } else if (userIsDistributor) {
                    profileType = 'DIST';
                    registerPayload = authSchemas.distributorRegister(data);
                } else {
                    profileType = 'PM';
                    registerPayload = authSchemas.companyRegister({
                        ...data,
                        subcategoryId: data.subcategoryId || subcategoryId,
                    });
                }
                let response;

                if (resubmit) {
                    // update user in our DB first as it may throw validation errors
                    // that do not exist in Cognito
                    response = await fetchWithBQ({
                        url: '/register/2',
                        method: 'POST',
                        data: registerPayload,
                    });

                    // update user attributes in Cognito
                    const payload = authSchemas.updateUserAttributes(data);
                    await Auth.updateUserAttributes({ userAttributes: payload });

                    // update store data
                    const user = await authApi.getMeEnriched(getInvestorNecessaryEnrichments());

                    const tunnelSubscription = userSelectors.getTunnelSubscription(getState());

                    // at this point the old BS document (if it existed) was deleted
                    // we need to clear the list
                    const documentsQueryObject = {
                        type: docTypes.SUBSCRIPTION_FORM,
                        subscriptionId: tunnelSubscription?.id,
                        pagination: false,
                    };
                    dispatch(
                        appApi.util.updateQueryData('getDocuments', documentsQueryObject, () => [])
                    );
                    // reset subscription form signature iframe
                    dispatch(subscriptionFormStatusSet(false));

                    dispatch(shareCategorySet(null));
                    dispatch(registerResubmitted({ user, profile: profileType }));
                } else {
                    // register user in our DB
                    response = await fetchWithBQ({
                        url: '/register/2',
                        method: 'POST',
                        data: registerPayload,
                    });

                    if (isCreatingSubscription && !response.error) {
                        // when a distributor creates a subscription
                        saveInStorage('route', '/subscriptions');
                        dispatch(
                            appApi.endpoints.impersonateUser.initiate({
                                userId: response.data.id,
                                indirectSubscription: true,
                            })
                        );
                    } else if (!response.error) {
                        // create user in Cognito
                        const payload = authSchemas.signUp(data);
                        await Auth.signUp(payload);
                    }
                }

                // error flow
                if (response.error) {
                    let { errors } = response.error.data;
                    const hasTunnelSteps = subscription?.userTunnelSteps?.length > 1;
                    if (
                        hasTunnelSteps ||
                        (resubmit &&
                            response?.error?.status &&
                            ![400, 401, 403, 500].includes(response?.error?.status))
                    ) {
                        // handling business errors from server:
                        // * the bad data is still saved in the DB so we need to update the store
                        const user = await authApi.getMeEnriched(getInvestorNecessaryEnrichments());
                        dispatch(registerResubmitted({ user, profile: profileType }));
                    }
                    dispatch(tunnelErrorsSet(errors));
                    return response;
                }

                return { data: null };
            },
            keepUnusedDataFor: 0,
        }),

        //#endregion

        //#region SGP COMPANIES
        createSGPCompany: build.mutation({
            async queryFn(payload, { dispatch, getState }, _extraOptions, fetchWithBQ) {
                const logoPayload = documentSchemas.sendDocuments(
                    payload.logo,
                    docTypes.SGP,
                    'logo'
                );
                const logoDoc = await documentApi.sendDocuments(logoPayload);

                const signaturePayload = documentSchemas.sendDocuments(
                    payload.signatureImage,
                    docTypes.SGP,
                    'signature'
                );
                const signatureDoc = await documentApi.sendDocuments(signaturePayload);

                const createPayload = {
                    name: payload.name.trim(),
                    logo: createDocumentsIRI(logoDoc?.[0]?.id),
                    signatureImage: createDocumentsIRI(signatureDoc?.[0]?.id),
                    rcsNumber: payload.rcsNumber,
                    rcsRegistrationAddress: payload.rcsRegistrationAddress,
                    amfData: payload.amfData,
                    legalRepresentativeName: payload.legalRepresentativeName,
                    legalRepresentativeFunction: payload.legalRepresentativeFunction,
                    contactEmail: payload.contactEmail,
                    documentsFooterText: payload.documentsFooterText,
                    bankIdentifier: payload.bankIdentifier,
                    addressLocation: payload.currentAddress,
                    distributorBillEmail: payload.distributorBillEmail,
                };

                const response = await fetchWithBQ({
                    url: '/sgps',
                    method: 'POST',
                    data: createPayload,
                });

                return response.data ? { data: null } : { error: response.error };
            },
            invalidatesTags: ['SGP'],
        }),

        updateSGPCompany: build.mutation({
            async queryFn(payload, { dispatch, getState }, _extraOptions, fetchWithBQ) {
                let logoIRI = null;
                let signatureIRI = null;

                if (payload.logo && payload.logo instanceof File) {
                    const logoPayload = documentSchemas.sendDocuments(
                        payload.logo,
                        docTypes.SGP,
                        'logo'
                    );
                    const logoDoc = await documentApi.sendDocuments(logoPayload);
                    logoIRI = createDocumentsIRI(logoDoc?.[0]?.id);
                } else if (payload.logo && payload.logo.id) {
                    logoIRI = createDocumentsIRI(payload.logo.id);
                }

                if (payload.signatureImage && payload.signatureImage instanceof File) {
                    const signaturePayload = documentSchemas.sendDocuments(
                        payload.signatureImage,
                        docTypes.SGP,
                        'signature'
                    );
                    const signatureDoc = await documentApi.sendDocuments(signaturePayload);
                    signatureIRI = createDocumentsIRI(signatureDoc?.[0]?.id);
                } else if (payload.signatureImage && payload.signatureImage.id) {
                    signatureIRI = createDocumentsIRI(payload.signatureImage.id);
                }

                const updatePayload = {
                    name: payload.name.trim(),
                    logo: logoIRI,
                    signatureImage: signatureIRI,
                    rcsNumber: payload.rcsNumber,
                    rcsRegistrationAddress: payload.rcsRegistrationAddress,
                    amfData: payload.amfData,
                    legalRepresentativeName: payload.legalRepresentativeName,
                    legalRepresentativeFunction: payload.legalRepresentativeFunction,
                    contactEmail: payload.contactEmail,
                    documentsFooterText: payload.documentsFooterText,
                    bankIdentifier: payload.bankIdentifier,
                    addressLocation: payload.currentAddress,
                    distributorBillEmail: payload.distributorBillEmail,
                };

                const response = await fetchWithBQ({
                    url: `/sgps/${payload.id}`,
                    method: 'PATCH',
                    data: updatePayload,
                });

                return response.data ? { data: null } : { error: response.error };
            },
            invalidatesTags: ['SGP'],
        }),

        getSGPs: build.query({
            query: payload => ({
                url: `/sgps?${qs.stringify(sgpSchemas.getSGPs(payload), queryStringifyOptions)}`,
            }),
            transformResponse: response => ({
                sgps: response.data,
                ...getPaginationData(response),
            }),
            providesTags: ['SGP'],
        }),

        getSGP: build.query({
            query: id => ({
                url: id ? `/sgps/${id}` : null,
            }),
            keepUnusedDataFor: 0,
            providesTags: ['SGP'],
        }),
        //#endregion

        //#region ANALYTICS

        getAggregatedSGPData: build.query({
            query: payload =>
                `/analytics/aggregated-servicer-data?${qs.stringify(
                    payload,
                    queryStringifyOptions
                )}`,
        }),

        getAggregatedProviderData: build.query({
            query: payload =>
                `/analytics/aggregated-provider-data?${qs.stringify(
                    payload,
                    queryStringifyOptions
                )}`,
        }),

        getAggregatedDistibutorData: build.query({
            query: payload =>
                `/analytics/aggregated-distributor-data?${qs.stringify(
                    payload,
                    queryStringifyOptions
                )}`,
            keepUnusedDataFor: 0,
            providesTags: ['Distributor', 'User'],
        }),

        getDistributorSubscribersData: build.query({
            query: payload =>
                `/analytics/subscription-distributor-data?${qs.stringify(
                    payload,
                    queryStringifyOptions
                )}`,
            keepUnusedDataFor: 0,
            providesTags: ['Distributor', 'User'],
        }),

        getDistributorSubscribersInvestments: build.query({
            query: payload =>
                `/analytics/distributor-subscriptions-invested-sums?${qs.stringify(
                    payload,
                    queryStringifyOptions
                )}`,
            keepUnusedDataFor: 0,
            providesTags: ['Distributor', 'User'],
        }),

        getDistributorSubscribersAllTimeInvestments: build.query({
            query: payload =>
                `/analytics/distributor-all-time-invested-sum?${qs.stringify(
                    payload,
                    queryStringifyOptions
                )}`,
            keepUnusedDataFor: 0,
            providesTags: ['Distributor', 'User'],
        }),

        getDistributorAdmissionFeeAnalytics: build.query({
            query: payload =>
                `/analytics/distributor-quarter-admission-fees?${qs.stringify(
                    payload,
                    queryStringifyOptions
                )}`,
            keepUnusedDataFor: 0,
            providesTags: ['Distributor', 'User'],
        }),

        getDistributorManagementFeeAnalytics: build.query({
            query: payload =>
                `/analytics/distributor-management-fees?${qs.stringify(
                    payload,
                    queryStringifyOptions
                )}`,
            keepUnusedDataFor: 0,
        }),

        //#endregion

        //#region TUNNEL STEPS

        getTunnelStep: build.query({
            query: id => ({ url: `/tunnel_steps/${id}` }),
            keepUnusedDataFor: 0,
            providesTags: ['Tunnel'],
        }),

        createCustomTunnelStep: build.mutation({
            async queryFn(
                { ppStepOrder, ppTunnelPhaseId, pmStepOrder, pmTunnelPhaseId, ...payload },
                _api,
                _extraOptions,
                fetchWithBQ
            ) {
                const ppTunnelPayload = {
                    order: ppStepOrder,
                    tunnelPhase: `/api/tunnel_phases/${ppTunnelPhaseId}`,
                    ...payload,
                    visibleForIndirectUntransferredSubscription: false,
                };
                const pmTunnelPayload = {
                    order: pmStepOrder,
                    tunnelPhase: `/api/tunnel_phases/${pmTunnelPhaseId}`,
                    ...payload,
                    visibleForIndirectUntransferredSubscription: false,
                };

                const response = await fetchWithBQ({
                    url: '/tunnel_steps',
                    method: 'POST',
                    data: ppTunnelPayload,
                });

                await fetchWithBQ({
                    url: '/tunnel_steps',
                    method: 'POST',
                    data: pmTunnelPayload,
                });

                // only one response is needed to check for errors, they're basically the same request
                return response.data ? { data: null } : { error: response.error };
            },
            invalidatesTags: ['Tunnel'],
        }),

        updateCustomTunnelStep: build.mutation({
            async queryFn(
                { ppStepOrder, pmStepOrder, ppId, pmId, ...payload },
                _api,
                _extraOptions,
                fetchWithBQ
            ) {
                const ppTunnelPayload = {
                    order: ppStepOrder,
                    ...payload,
                };
                const pmTunnelPayload = {
                    order: pmStepOrder,
                    ...payload,
                };

                const response = await fetchWithBQ({
                    url: `/tunnel_steps/${ppId}`,
                    method: 'PATCH',
                    data: ppTunnelPayload,
                });

                await fetchWithBQ({
                    url: `/tunnel_steps/${pmId}`,
                    method: 'PATCH',
                    data: pmTunnelPayload,
                });

                // only one response is needed to check for errors, they're basically the same request
                return response.data ? { data: null } : { error: response.error };
            },
            invalidatesTags: ['Tunnel'],
        }),

        deleteCustomTunnelStep: build.mutation({
            async queryFn({ ppId, pmId }, _api, _extraOptions, fetchWithBQ) {
                const response = await fetchWithBQ({
                    url: `/tunnel_steps/${ppId}`,
                    method: 'DELETE',
                });

                await fetchWithBQ({
                    url: `/tunnel_steps/${pmId}`,
                    method: 'DELETE',
                });

                // only one response is needed to check for errors, they're basically the same request
                return response.data ? { data: null } : { error: response.error };
            },
            invalidatesTags: ['Tunnel'],
        }),

        //#endregion

        //#region QUESTIONS

        getQuestions: build.query({
            query: payload => ({
                url: `/questions?${qs.stringify(payload, queryStringifyOptions)}`,
            }),
            transformResponse: response =>
                response.map(q => ({
                    ...q,
                    answers: q.answers.sort(sortArrayByKey('order')),
                })),
            providesTags: ['Question'],
        }),

        createQuestion: build.mutation({
            async queryFn({ ppId, pmId, ...payload }, _api, _extraOptions, fetchWithBQ) {
                const response = await fetchWithBQ({
                    url: '/questions',
                    method: 'POST',
                    data: { ...payload, tunnelStep: `api/tunnel_steps/${ppId}` },
                });

                await fetchWithBQ({
                    url: '/questions',
                    method: 'POST',
                    data: { ...payload, tunnelStep: `api/tunnel_steps/${pmId}` },
                });

                // only one response is needed to check for errors, they're basically the same request
                return response.data ? { data: null } : { error: response.error };
            },
            invalidatesTags: ['Question'],
        }),

        updateQuestion: build.mutation({
            async queryFn(
                { ppQuestionId, pmQuestionId, tunnelStep, answers, ...payload }, // tunnelStep and answers should not be included in the payload
                _api,
                _extraOptions,
                fetchWithBQ
            ) {
                const response = await fetchWithBQ({
                    url: `/questions/${ppQuestionId}`,
                    method: 'PATCH',
                    data: payload,
                });

                await fetchWithBQ({
                    url: `/questions/${pmQuestionId}`,
                    method: 'PATCH',
                    data: payload,
                });

                // only one response is needed to check for errors, they're basically the same request
                return response.data ? { data: null } : { error: response.error };
            },
            invalidatesTags: ['Question'],
        }),

        deleteQuestion: build.mutation({
            async queryFn({ ppId, pmId }, _api, _extraOptions, fetchWithBQ) {
                const response = await fetchWithBQ({
                    url: `/questions/${ppId}`,
                    method: 'DELETE',
                });

                await fetchWithBQ({
                    url: `/questions/${pmId}`,
                    method: 'DELETE',
                });

                // only one response is needed to check for errors, they're basically the same request
                return response.data ? { data: null } : { error: response.error };
            },
            invalidatesTags: ['Question'],
        }),

        //#endregion

        //#region ANSWERS

        getAnswers: build.query({
            query: payload => ({
                url: `/answers?${qs.stringify(payload, queryStringifyOptions)}`,
            }),
            providesTags: ['Answer'],
        }),

        createAnswer: build.mutation({
            async queryFn(
                { ppQuestionId, pmQuestionId, ...payload },
                _api,
                _extraOptions,
                fetchWithBQ
            ) {
                const ppAnswerPayload = {
                    question: `api/questions/${ppQuestionId}`,
                    ...payload,
                };
                const pmAnswerPayload = {
                    question: `api/questions/${pmQuestionId}`,
                    ...payload,
                };

                const response = await fetchWithBQ({
                    url: '/answers',
                    method: 'POST',
                    data: ppAnswerPayload,
                });

                await fetchWithBQ({
                    url: '/answers',
                    method: 'POST',
                    data: pmAnswerPayload,
                });

                // only one response is needed to check for errors, they're basically the same request
                return response.data ? { data: null } : { error: response.error };
            },
            invalidatesTags: ['Answer', 'Question'],
        }),

        updateAnswer: build.mutation({
            async queryFn(
                { ppId, pmId, question, ...payload }, // question should not be included in the payload
                _api,
                _extraOptions,
                fetchWithBQ
            ) {
                const response = await fetchWithBQ({
                    url: `/answers/${ppId}`,
                    method: 'PATCH',
                    data: payload,
                });

                await fetchWithBQ({
                    url: `/answers/${pmId}`,
                    method: 'PATCH',
                    data: payload,
                });

                // only one response is needed to check for errors, they're basically the same request
                return response.data ? { data: null } : { error: response.error };
            },
            invalidatesTags: ['Answer', 'Question'],
        }),

        deleteAnswer: build.mutation({
            async queryFn({ ppId, pmId }, _api, _extraOptions, fetchWithBQ) {
                const response = await fetchWithBQ({
                    url: `/answers/${ppId}`,
                    method: 'DELETE',
                });

                await fetchWithBQ({
                    url: `/answers/${pmId}`,
                    method: 'DELETE',
                });

                // only one response is needed to check for errors, they're basically the same request
                return response.data ? { data: null } : { error: response.error };
            },
            invalidatesTags: ['Answer', 'Question'],
        }),

        //#endregion

        //#region TUNNEL ACTIONS

        getTunnelActions: build.query({
            query: payload => ({
                url: `/tunnel_actions?${qs.stringify(payload, queryStringifyOptions)}`,
            }),
            providesTags: ['Action'],
        }),

        createTunnelAction: build.mutation({
            async queryFn(
                {
                    ppTunnelId,
                    pmTunnelId,
                    ppQuestions,
                    pmQuestions,
                    conditions,
                    questions,
                    ...payload
                },
                _api,
                _extraOptions,
                fetchWithBQ
            ) {
                const ppActionPayload = {
                    tunnel: `api/tunnels/${ppTunnelId}`,
                    questions: ppQuestions,
                    actionConditions: conditions,
                    ...payload,
                };

                const response = await fetchWithBQ({
                    url: '/tunnel_actions',
                    method: 'POST',
                    data: ppActionPayload,
                });

                const pmActionPayload = {
                    tunnel: `api/tunnels/${pmTunnelId}`,
                    questions: pmQuestions,
                    actionConditions: conditions,
                    matchingTunnelAction: `/api/tunnel_actions/${response.data.id}`,
                    ...payload,
                };

                const pmActionResponse = await fetchWithBQ({
                    url: '/tunnel_actions',
                    method: 'POST',
                    data: pmActionPayload,
                });

                await fetchWithBQ({
                    url: `/tunnel_actions/${response.data.id}`,
                    method: 'PATCH',
                    data: {
                        matchingTunnelAction: `/api/tunnel_actions/${pmActionResponse.data.id}`,
                    },
                });

                // only one response is needed to check for errors, they're basically the same request
                return response.data ? { data: null } : { error: response.error };
            },
            invalidatesTags: ['Action'],
        }),

        updateTunnelAction: build.mutation({
            async queryFn(
                { ppId, pmId, ppQuestions, pmQuestions, action }, // action should not be included in the payload
                _api,
                _extraOptions,
                fetchWithBQ
            ) {
                const ppPayload = { questions: ppQuestions, ...action };
                const response = await fetchWithBQ({
                    url: `/tunnel_actions/${ppId}`,
                    method: 'PATCH',
                    data: ppPayload,
                });

                const pmPayload = { questions: pmQuestions, ...action };

                await fetchWithBQ({
                    url: `/tunnel_actions/${pmId}`,
                    method: 'PATCH',
                    data: pmPayload,
                });

                return response.data ? { data: null } : { error: response.error };
            },
            invalidatesTags: ['Action'],
        }),

        deleteTunnelAction: build.mutation({
            async queryFn({ ppId, pmId }, _api, _extraOptions, fetchWithBQ) {
                const response = await fetchWithBQ({
                    url: `/tunnel_actions/${ppId}`,
                    method: 'DELETE',
                });

                await fetchWithBQ({
                    url: `/tunnel_actions/${pmId}`,
                    method: 'DELETE',
                });

                return response.data ? { data: null } : { error: response.error };
            },
            invalidatesTags: ['Action'],
        }),

        //#endregion

        //#region ACTION CONDITIONS

        getActionConditions: build.query({
            query: payload => ({
                url: `/action_conditions?${qs.stringify(payload, queryStringifyOptions)}`,
            }),
            providesTags: ['Condition'],
        }),

        createActionCondition: build.mutation({
            async queryFn(
                { ppActionId, pmActionId, limitValue, ...payload },
                _api,
                _extraOptions,
                fetchWithBQ
            ) {
                const ppConditionPayload = {
                    tunnelAction: `api/tunnel_actions/${ppActionId}`,
                    limitValue: +limitValue,
                    ...payload,
                };
                const pmConditionPayload = {
                    tunnelAction: `api/tunnel_actions/${pmActionId}`,
                    limitValue: +limitValue,
                    ...payload,
                };

                const response = await fetchWithBQ({
                    url: '/action_conditions',
                    method: 'POST',
                    data: ppConditionPayload,
                });

                await fetchWithBQ({
                    url: '/action_conditions',
                    method: 'POST',
                    data: pmConditionPayload,
                });

                // only one response is needed to check for errors, they're basically the same request
                return response.data ? { data: null } : { error: response.error };
            },
            invalidatesTags: ['Condition'],
        }),

        updateActionCondition: build.mutation({
            async queryFn({ ppId, pmId, ...payload }, _api, _extraOptions, fetchWithBQ) {
                const response = await fetchWithBQ({
                    url: `/action_conditions/${ppId}`,
                    method: 'PATCH',
                    data: payload,
                });

                await fetchWithBQ({
                    url: `/action_conditions/${pmId}`,
                    method: 'PATCH',
                    data: payload,
                });

                return response.data ? { data: null } : { error: response.error };
            },
            invalidatesTags: ['Condition', 'Action'],
        }),

        deleteActionCondition: build.mutation({
            async queryFn({ ppId, pmId }, _api, _extraOptions, fetchWithBQ) {
                const response = await fetchWithBQ({
                    url: `/action_conditions/${ppId}`,
                    method: 'DELETE',
                });

                await fetchWithBQ({
                    url: `/action_conditions/${pmId}`,
                    method: 'DELETE',
                });

                return response.data ? { data: null } : { error: response.error };
            },
            invalidatesTags: ['Condition'],
        }),

        //#endregion

        //#region PROMOTERS

        getPromoters: build.query({
            query: payload => ({
                url: `/promoters?${qs.stringify(payload, queryStringifyOptions)}`,
            }),
            transformResponse: response => ({
                promoters: response,
            }),
            providesTags: ['Promoter'],
        }),

        getPromoter: build.query({
            query: id => ({
                url: id ? `/promoters/${id}` : null,
            }),
            keepUnusedDataFor: 0,
            providesTags: ['Promoter'],
        }),

        createPromoter: build.mutation({
            async queryFn(payload, { dispatch, getState }, _extraOptions, fetchWithBQ) {
                const logoPayload = documentSchemas.sendDocuments(
                    payload.logoFile,
                    docTypes.PROMOTER,
                    'logo'
                );
                const logoDoc = await documentApi.sendDocuments(logoPayload);

                const createPayload = {
                    name: payload.name.trim(),
                    contactEmail: payload.contactEmail.trim(),
                    logo: createDocumentsIRI(logoDoc?.[0]?.id),
                };

                const response = await fetchWithBQ({
                    url: '/promoters',
                    method: 'POST',
                    data: createPayload,
                });

                return response.data ? { data: null } : { error: response.error };
            },
            invalidatesTags: ['Promoter'],
        }),

        updatePromoter: build.mutation({
            async queryFn(payload, { dispatch, getState }, _extraOptions, fetchWithBQ) {
                let updatePayload = {
                    name: payload.name.trim(),
                    contactEmail: payload.contactEmail.trim(),
                };
                if (!payload.logo && payload.logoFile && payload.logoFile instanceof File) {
                    const logoPayload = documentSchemas.sendDocuments(
                        payload.logoFile,
                        docTypes.PROMOTER,
                        'logo'
                    );
                    const logoDoc = await documentApi.sendDocuments(logoPayload);
                    updatePayload.logo = createDocumentsIRI(logoDoc?.[0]?.id);
                }

                const response = await fetchWithBQ({
                    url: `/promoters/${payload.id}`,
                    method: 'PATCH',
                    data: updatePayload,
                });

                return response.data ? { data: null } : { error: response.error };
            },
            invalidatesTags: ['Promoter'],
        }),

        contactPromoter: build.mutation({
            query: ({ promoterId, fundId }) => ({
                url: `/promoters/${promoterId}/investment_funds/${fundId}/contact`,
                method: 'POST',
                data: {},
            }),
        }),

        //#endregion

        //#region NET ASSET VALUES

        getNAV: build.query({
            query: payload => ({
                url: `/net_asset_values?${qs.stringify(payload, queryStringifyOptions)}`,
            }),
            providesTags: ['NAV'],
        }),

        createNAV: build.mutation({
            async queryFn(_args, _api, _extraOptions, fetchWithBQ) {
                const { form, formDate } = _args;
                const items = Object.values(form);
                const promises = [];

                for (let capitalAccount of items) {
                    const payload = capitalAccountSchemas.addCapitalAccount(
                        capitalAccount,
                        formDate
                    );
                    promises.push(
                        fetchWithBQ({
                            url: '/net_asset_values',
                            method: 'POST',
                            data: payload,
                        })
                    );
                }

                await Promise.all(promises);

                return { data: null };
            },
            invalidatesTags: ['NAV'],
        }),

        updateNAV: build.mutation({
            async queryFn(_args, _api, _extraOptions, fetchWithBQ) {
                const { form, formDate } = _args;
                const items = Object.values(form);
                const promises = [];

                for (let nav of items) {
                    const payload = capitalAccountSchemas.addCapitalAccount(nav, formDate);
                    promises.push(
                        fetchWithBQ({
                            url: `/net_asset_values/${nav.id}`,
                            method: 'PATCH',
                            data: payload,
                        })
                    );
                }

                await Promise.all(promises);

                return { data: null };
            },
            invalidatesTags: ['NAV'],
        }),

        testNAV: build.mutation({
            query: ({ id, ...payload }) => ({
                url: `/net-asset-values/${id}/test`,
                method: 'POST',
                data: payload,
            }),
            invalidatesTags: ['NAV'],
        }),

        simulateNAV: build.mutation({
            async queryFn({ id, ...payload }, _api, _extraOptions, fetchWithBQ) {
                // make the initial request
                const { data: response } = await fetchWithBQ({
                    url: `/net-asset-values/${id}/simulate`,
                    method: 'POST',
                    data: payload,
                    responseType: 'arraybuffer',
                });

                if (response.data) {
                    // open downloaded file
                    exportData(response.data, response.headers);
                }

                return { data: null };
            },
        }),

        activateNAV: build.mutation({
            query: ({ id, ...payload }) => ({
                url: `/net-asset-values/${id}/activate`,
                method: 'POST',
                data: payload,
            }),
            invalidatesTags: ['NAV'],
        }),

        //#endregion

        //#region RETROCESSIONS

        getRetrocessions: build.query({
            query: payload => ({
                url: `/retrocessions?${qs.stringify(payload, queryStringifyOptions)}`,
            }),
            transformResponse: response => ({
                retrocessions: response.data,
                ...getPaginationData(response),
            }),
            providesTags: ['Retrocession'],
        }),

        exportRetrocessions: build.mutation({
            async queryFn(payload, _api, _extraOptions, fetchWithBQ) {
                const { data: response } = await fetchWithBQ({
                    url: '/retrocessions/export',
                    method: 'POST',
                    data: {},
                    responseType: 'arraybuffer',
                });

                if (response.data) {
                    // open downloaded file
                    exportData(response.data, response.headers);
                }

                return { data: null };
            },
        }),

        //#endregion
    }),
    overrideExisting: false,
});

export const {
    // comments
    useCreateCommentMutation,

    // banking statements
    useUpdateBankingDataMutation,
    useUpdateBankingStatementMutation,
    useUpdateBankingStatementValidationMutation,
    useReinitiateDDASignatureMutation,
    useInitiateNewDDASignatureMutation,

    // roles
    useGetRolesQuery,

    // investment funds
    useGetInvestmentFundQuery,
    useGetInvestmentFundsQuery,
    useRequestProductDistributionMutation,
    useCreateInvestmentFundMutation,
    useUpdateInvestmentFundMutation,
    useAssignPrivateFundToDistributorsMutation,

    // referrals
    useGetReferralsQuery,
    useCreateReferralMutation,
    useExportReferralsMutation,

    // subscription alerts
    useCreateSubscriptionAlertMutation,
    useUpdateSubscriptionAlertMutation,

    // conventions
    useGetConventionsQuery,
    useCreateConventionMutation,
    useUpdateConventionMutation,
    useInitiateConventionSignatureMutation,

    // users
    useGetUsersQuery,
    useGetUserQuery,
    useGetUserLightQuery,
    useGetUserPortfolioQuery,
    useGetAdminsQuery,
    useGetDistributorUsersQuery,
    useGetInvestmentCharacteristicsQuery,
    useDeleteUserMutation,
    useUpdateUserStatusMutation,
    useInviteUserMutation,
    useResendInvitationMutation,
    useUpdatePasswordMutation,
    useImpersonateUserMutation,
    useStopImpersonationMutation,
    useCheckEmailAvailabilityMutation,
    useUpdateInvestmentCharacteristicsMutation,
    useUpdateInvestorProfileMutation,
    useUpdateUserMutation,
    useExportInvestorsMutation,
    useExportPortfolioMutation,

    // distributor companies
    useGetDistributorCompaniesQuery,
    useGetDistributorCompanyQuery,
    useCreateSpecialDistributorMutation,
    useUpdateSpecialDistributorMutation,
    useUpdateDistributorCompanyMutation,
    useUpdateDistributorCommercialMutation,
    useDeleteDistributorCompanyMutation,
    useGetDistributorCompanyEnrichedQuery,
    useExportDistributorCompaniesMutation,
    useImportDistributorUsersMutation,
    useValidateInscriptionMutation,

    // distributor bills
    useGetDistributorBillsQuery,
    useTestDistributorBillsMutation,
    useSimulateDistributorBillsMutation,
    useActivateDistributorBillsMutation,

    // subscriptions
    useGetSubscriptionsQuery,
    useCreateSubscriptionMutation,
    useCreateExternalSubscriptionMutation,
    useAddIndirectSubscriptionMutation,
    useUpdateSubscriptionMutation,
    useTransferSubscriptionMutation,
    useMoveSubscriptionMutation,
    useSuspendSubscriptionMutation,
    useReopenSubscriptionMutation,
    useDeleteSubscriptionMutation,
    useExportSubscriptionMutation,
    useExportSubscriptionsMutation,
    useValidateSubscriptionMutation,
    useVerifySubscriptionMutation,

    // signatures
    useUpdateSignatureMutation,

    // capital calls
    useGetCapitalCallsQuery,
    useCreateCapitalCallMutation,
    useUpdateCapitalCallMutation,
    useDeleteCapitalCallMutation,
    useSimulateCapitalCallMutation,
    useTestCapitalCallMutation,
    useActivateCapitalCallMutation,
    useDownloadCapitalCallGeneratedDocumentsMutation,
    useTraceCapitalCallMutation,
    useSendCapitalCallDocumentsMutation,

    // reportings
    useGetReportingsQuery,
    useCreateReportingMutation,
    useUpdateReportingMutation,
    useTestReportingMutation,
    useActivateReportingMutation,

    // capital accounts
    useGetCapitalAccountsQuery,
    useCreateCapitalAccountMutation,
    useUpdateCapitalAccountMutation,
    useTestCapitalAccountMutation,
    useSimulateCapitalAccountMutation,
    useActivateCapitalAccountMutation,

    // notifications
    useGetNotificationsQuery,
    useGetNotificationQuery,
    useCreateNotificationMutation,
    useCreateMassNotificationMutation,
    useSendSMSMutation,
    useRequestUpcomingProductNotificationMutation,

    // payments
    useGetPaymentsQuery,
    useCreatePartialPaymentMutation,
    useUpdatePaymentMutation,
    useCreateDirectDebitMutation,
    useExportPaymentsMutation,

    // netowrks
    useGetNetworksQuery,
    useGetNetworkQuery,
    useCreateNetworkMutation,
    useUpdateNetworkMutation,
    useDeleteNetworkMutation,

    // documents
    useGetDocumentsQuery,
    useGetDocumentQuery,
    useGetDocumentAuditsQuery,
    useCreateDocumentsMutation,
    useReplaceDocumentMutation,
    useDeleteDocumentMutation,
    useValidateDocumentMutation,
    useStartReplaceIdVerificationMutation,
    useStartReplaceAddressVerificationMutation,
    useSendReplaceIdVerificationMutation,
    useSendReplaceAddressVerificationMutation,

    // share categories
    useGetShareCategoriesQuery,
    useCreateShareCategoryMutation,
    useUpdateShareCategoryMutation,
    useDeleteShareCategoryMutation,
    useGetShareSubcategoriesQuery,
    useGetShareSubcategoryQuery,
    useCreateShareSubcategoryMutation,
    useUpdateShareSubcategoryMutation,
    useDeleteShareSubcategoryMutation,

    // closings
    useGetClosingsQuery,
    useCreateClosingMutation,
    useUpdateClosingMutation,
    useSimulateClosingMutation,
    useActivateClosingMutation,
    useTestClosingMutation,
    useDeleteClosingMutation,

    // tunnel
    useCheckDistributorPedagogicStepMutation,
    useGetTunnelMessagesQuery,
    useGetTunnelQuery,
    useGetTunnelsQuery,
    useCloneInvestmentFundTunnelMutation,
    useUpdateCustomTunnelMutation,
    useDeletePendingTunnelMutation,
    useActivateCustomTunnelMutation,

    // authentication
    useSetPasswordMutation,
    useCreatePasswordMutation,
    useForgotPasswordMutation,
    useResetPasswordMutation,
    useResendSignUpMutation,
    useConfirmSignUpMutation,
    useCognitoSignInMutation,
    useSignOutMutation,
    useGetCurrentUserQuery,
    useLazyGetCurrentUserQuery,
    useSignInMutation,
    useConfirmSignInMutation,
    useSignUpMutation,

    // SGP compannies
    useCreateSGPCompanyMutation,
    useUpdateSGPCompanyMutation,
    useGetSGPsQuery,
    useGetSGPQuery,

    // analytics
    useGetAggregatedSGPDataQuery,
    useGetAggregatedProviderDataQuery,
    useGetAggregatedDistibutorDataQuery,
    useGetDistributorSubscribersAllTimeInvestmentsQuery,
    useGetDistributorSubscribersDataQuery,
    useGetDistributorSubscribersInvestmentsQuery,
    useGetDistributorAdmissionFeeAnalyticsQuery,
    useGetDistributorManagementFeeAnalyticsQuery,

    // tunnel steps
    useGetTunnelStepQuery,
    useCreateCustomTunnelStepMutation,
    useUpdateCustomTunnelStepMutation,
    useDeleteCustomTunnelStepMutation,

    // questions
    useGetQuestionsQuery,
    useCreateQuestionMutation,
    useUpdateQuestionMutation,
    useDeleteQuestionMutation,

    // answers
    useGetAnswersQuery,
    useCreateAnswerMutation,
    useUpdateAnswerMutation,
    useDeleteAnswerMutation,

    // tunnel actions
    useGetTunnelActionsQuery,
    useCreateTunnelActionMutation,
    useUpdateTunnelActionMutation,
    useDeleteTunnelActionMutation,

    // action conditions
    useGetActionConditionsQuery,
    useCreateActionConditionMutation,
    useUpdateActionConditionMutation,
    useDeleteActionConditionMutation,

    // promoters
    useGetPromotersQuery,
    useGetPromoterQuery,
    useCreatePromoterMutation,
    useUpdatePromoterMutation,
    useGetAggregatedPromoterDataQuery,
    useContactPromoterMutation,

    // net asset values
    useGetNAVQuery,
    useCreateNAVMutation,
    useUpdateNAVMutation,
    useTestNAVMutation,
    useSimulateNAVMutation,
    useActivateNAVMutation,

    // retrocessions
    useGetRetrocessionsQuery,
    useExportRetrocessionsMutation,
} = mainApi;

export default mainApi;
